import React, { useRef, useEffect, useState } from 'react';
import * as THREE from 'three'; // Keep for HeroSection

// --- Helper: Simple Random Float ---
function getRandomFloat(min, max) {
  return Math.random() * (max - min) + min;
}

// --- TOP-LEVEL CONSTANTS FOR CSS INJECTION ---
const GLOBAL_CORE_APPEAR_DURATION = 0.6;
const GLOBAL_STREAM_GROW_DURATION = 0.8;
const GLOBAL_PULSE_DURATION = 1.5;
const GLOBAL_RING_EXPAND_DURATION = 3.0;
const GLOBAL_ORBIT_DURATION_FAST = 4.0;
const GLOBAL_ORBIT_DURATION_SLOW = 7.0;
// Platform Constants (Minimalist Style - REVAMPED)
const GLOBAL_PLATFORM_SECTION_FADE_IN = 0.8;
const GLOBAL_PLATFORM_LIST_ITEM_STAGGER = 0.05;
const GLOBAL_PLATFORM_DETAILS_FADE = 0.5;
const GLOBAL_PLATFORM_FLOW_NODE_APPEAR = 0.4;
const GLOBAL_PLATFORM_FLOW_NODE_STAGGER = 0.15;
const GLOBAL_PLATFORM_FLOW_LINE_DRAW = 0.5;
const GLOBAL_PLATFORM_FLOW_LINE_DELAY = 0.3;
const GLOBAL_PLATFORM_FLOW_LINE_STAGGER = 0.1;
// Ecosystem Constants
const GLOBAL_ECO_HUB_PULSE_DURATION = 3.5;
const GLOBAL_ECO_ICON_PULSE_DURATION = 4.0;
const GLOBAL_ECO_LINE_DRAW_DURATION = 0.8;
// Security Constants
const GLOBAL_SECURITY_WEAVE_FLOW_DURATION = 10.0;
const GLOBAL_SECURITY_SPARK_TRAVEL_DURATION = 5.0;
const GLOBAL_SECURITY_NODE_PULSE_DURATION = 3.0;
const GLOBAL_SECURITY_GLOW_PULSE_DURATION = 4.0;


// --- Navigation Helper ---
const handleNavigate = (target) => {
  if (target.startsWith('#')) {
    const element = document.querySelector(target);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      console.warn(`Navigation target ${target} not found.`);
    }
  } else if (target.startsWith('/') && !target.startsWith('//')) {
    window.location.href = target; // Navigate to internal pages like /login
  } else {
    window.open(target, '_blank', 'noopener noreferrer'); // Open external links
  }
};

// --- Loading Animation Component ---
function LoadingAnimation({ onLoadComplete }) {
  const [loadProgress, setLoadProgress] = useState(0);
  const [showMainContent, setShowMainContent] = useState(false);
  const canvasRef = useRef(null);
  
  // Animation stages
  const [animationStage, setAnimationStage] = useState(0);
  // 0: Initial blank
  // 1: Logo appears
  // 2: Animation completes
  
  useEffect(() => {
    // Logo animation
    let ctx = null;
    let animationFrameId = null;
    let particles = [];
    const FPS = 60;
    
    const init = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      ctx = canvas.getContext('2d');
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      // Create particles in a grid formation
      const numCols = Math.floor(canvas.width / 25);
      const numRows = Math.floor(canvas.height / 25);
      const spacingX = canvas.width / numCols;
      const spacingY = canvas.height / numRows;
      
      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;
      
      // Create particles
      for (let i = 0; i < numCols; i++) {
        for (let j = 0; j < numRows; j++) {
          const x = i * spacingX;
          const y = j * spacingY;
          const dx = x - centerX;
          const dy = y - centerY;
          const distance = Math.sqrt(dx * dx + dy * dy);
          
          if (distance < Math.min(canvas.width, canvas.height) * 0.4) {
            particles.push({
              x: x,
              y: y,
              size: 1.2,
              baseX: x,
              baseY: y,
              density: (Math.random() * 30) + 1,
              color: { r: 145, g: 55, b: 220, a: 0 },
              targetColor: { r: 145, g: 55, b: 220, a: 0.7 }
            });
          }
        }
      }
      
      // Start animation sequence
      startSequence();
      animate();
    };
    
    const startSequence = () => {
      setTimeout(() => {
        setAnimationStage(1); // Logo appears
        
        particles.forEach(p => {
          // Add null check before accessing canvasRef.current
          if (!canvasRef.current) return;
          
          const centerX = canvasRef.current.width / 2;
          const centerY = canvasRef.current.height / 2;
          const distance = Math.sqrt(
            Math.pow(p.baseX - centerX, 2) + 
            Math.pow(p.baseY - centerY, 2)
          );
          const delay = distance * 0.002;
          setTimeout(() => {
            // Add another null check since this runs after a delay
            if (!canvasRef.current) return;
            p.targetColor.a = 0.6 - (distance / (canvasRef.current.width));
            if (p.targetColor.a < 0) p.targetColor.a = 0;
          }, delay * 1000);
        });
        
        // Complete animation faster
        setTimeout(() => {
          setAnimationStage(2); // Complete
          
          particles.forEach(p => {
            const angle = Math.random() * Math.PI * 2;
            const force = 10 + Math.random() * 20;
            p.vx = Math.cos(angle) * force;
            p.vy = Math.sin(angle) * force;
            p.targetColor.a = 0;
          });
          
          setTimeout(() => {
            setShowMainContent(true);
            if (onLoadComplete) onLoadComplete();
          }, 600);
        }, 1500);
      }, 300);
    };
    
    const animate = () => {
      if (!ctx || !canvasRef.current) {
        // If canvas or context is null, try again in the next frame but don't draw
        animationFrameId = requestAnimationFrame(animate);
        return;
      }
      
      // Clear canvas
      ctx.fillStyle = 'rgba(0, 0, 0, 1)';
      ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      
      const time = Date.now() * 0.001;
      
      // Update and draw particles
      particles.forEach(p => {
        // If in dispersion stage
        if (animationStage === 2 && p.vx !== undefined) {
          p.x += p.vx * 0.05;
          p.y += p.vy * 0.05;
          p.vx *= 0.98;
          p.vy *= 0.98;
        }
        
        // Update colors
        p.color.r += (p.targetColor.r - p.color.r) * 0.05;
        p.color.g += (p.targetColor.g - p.color.g) * 0.05;
        p.color.b += (p.targetColor.b - p.color.b) * 0.05;
        p.color.a += (p.targetColor.a - p.color.a) * 0.05;
        
        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(${p.color.r}, ${p.color.g}, ${p.color.b}, ${p.color.a})`;
        ctx.fill();
      });
      
      // Simulate loading progress faster
      if (loadProgress < 100) {
        setLoadProgress(prev => {
          const increment = (animationStage === 0) ? 0.5 : 
                           (animationStage === 1) ? 2.0 : 5;
          return Math.min(prev + increment, 100);
        });
      }
      
      // Schedule next frame
      setTimeout(() => {
        animationFrameId = requestAnimationFrame(animate);
      }, 1000 / FPS);
    };
    
    // Handle window resize
    const handleResize = () => {
      if (canvasRef.current) {
        canvasRef.current.width = window.innerWidth;
        canvasRef.current.height = window.innerHeight;
        
        // Reinitialize animation on window resize to keep it looking good
        if (animationStage < 2) {
          // Only reinitialize if we're still in the animation phase
          particles = [];
          init();
        }
      }
    };
    
    window.addEventListener('resize', handleResize);
    
    // Initialize and start animation
    init();
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [animationStage, loadProgress, onLoadComplete]);
  
  if (showMainContent) {
    return null;
  }
  
  return (
    <div className={`fixed inset-0 bg-black z-50 flex flex-col items-center justify-center transition-opacity duration-1000 ${animationStage === 2 ? 'opacity-0' : 'opacity-100'}`}>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full" />
      
      <div className="relative z-10 text-center px-4">
        <div className={`transition-all duration-1000 transform ${animationStage >= 1 ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-2">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-purple-400 to-purple-600">Metabees</span> AI Labs
          </h1>
        </div>
        
        <div className={`transition-all duration-1000 transform ${animationStage >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'}`} style={{ transitionDelay: '200ms' }}>
          <p className="text-xl text-gray-300 mb-8 font-light tracking-wide">
            <span className="text-purple-300">Intelligence</span> ⋅ <span className="text-cyan-300">Automation</span> ⋅ <span className="text-fuchsia-300">Precision</span>
          </p>
        </div>
        
        <div className="relative w-64 h-1 bg-gray-800/50 rounded-full overflow-hidden mx-auto">
          <div 
            className="absolute top-0 left-0 h-full bg-gradient-to-r from-purple-600 via-purple-400 to-fuchsia-500 rounded-full transition-all duration-200"
            style={{ width: `${loadProgress}%` }}
          />
        </div>
      </div>
    </div>
  );
}

// --- Notification Components ---

function AnnouncementBanner() {
  return (
    <div className="bg-gradient-to-r from-purple-900/50 via-black to-purple-900/50 text-purple-300 py-1.5 px-4 border-b border-gray-800 text-sm text-center">
       <span className="font-semibold mr-1.5">🎉 Announcement:</span> Metabees becomes the first company in India to release a production-ready reasoning LLM!
    </div>
  );
}

function UpdatePopup({ onClose }) {
  return (
    <div className="fixed top-4 right-4 z-50 max-w-sm w-[calc(100%-2rem)] sm:w-auto">
      <div className="bg-gray-950 text-gray-300 p-4 rounded-md shadow-xl border border-gray-700 transition-all duration-300 ease-out transform translate-y-0 opacity-100">
        <div className="flex justify-between items-start">
          <div className="flex-grow pr-3">
            <p className="text-sm font-medium text-purple-400 mb-1">Important Update</p>
            <p className="text-xs leading-relaxed">
              Echidna Mail is being phased out and replaced by <span className="font-medium text-purple-300">Anne SDR</span>, effective August 1st. Please plan your transition.
            </p>
          </div>
          <div
            onClick={onClose}
            className="flex-shrink-0 cursor-pointer text-gray-600 hover:text-gray-300 transition-colors p-1 -mt-1 -mr-1 rounded-full hover:bg-gray-800"
            title="Close notification"
            role="button"
            tabIndex={0}
            onKeyPress={(e) => e.key === 'Enter' && onClose()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
        </div>
      </div>
    </div>
  );
}


// --- Section Components ---

// Hero Section
function HeroSection() {
  const mountRef = useRef(null); 
  const mouse = useRef({ x: 0, y: 0 });
  const [interactionCount, setInteractionCount] = useState(0);
  
  useEffect(() => { 
    const currentMount = mountRef.current; 
    if (!currentMount) return; 
    
    let scene, camera, renderer, particles, animationFrameId, clock; 
    let geometry, material;
    let particleGroups = [];
    let particleCount = 5000;
    let activeColorScheme = 0;
    let lastInteractionTime = 0;
    let particleClickEnabled = true;
    
    // Color schemes for particle variations
    const colorSchemes = [
      { base: new THREE.Color(0x9333ea), accent: new THREE.Color(0xc084fc) }, // Purple
      { base: new THREE.Color(0x0ea5e9), accent: new THREE.Color(0x38bdf8) }, // Blue
      { base: new THREE.Color(0x14b8a6), accent: new THREE.Color(0x2dd4bf) }, // Teal
      { base: new THREE.Color(0xf59e0b), accent: new THREE.Color(0xfbbf24) }, // Amber
    ];
    
    const initThree = () => { 
      scene = new THREE.Scene(); 
      camera = new THREE.PerspectiveCamera(75, currentMount.clientWidth / currentMount.clientHeight, 0.1, 1000); 
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true }); 
      renderer.setSize(currentMount.clientWidth, currentMount.clientHeight); 
      renderer.setPixelRatio(window.devicePixelRatio); 
      currentMount.appendChild(renderer.domElement); 
      
      createParticleSystem();
      
      camera.position.z = 1; 
      clock = new THREE.Clock(); 
      animate(); 
    }; 
    
    const createParticleSystem = () => {
      // Clear existing particles if any
      if (particles) {
        scene.remove(particles);
        if (geometry) geometry.dispose();
        if (material) material.dispose();
        particleGroups = [];
      }
      
      const colorScheme = colorSchemes[activeColorScheme];
      
      // Create two particle groups - one for base particles, one for interactive
      const positions = new Float32Array(particleCount * 3);
      const colors = new Float32Array(particleCount * 3);
      const sizes = new Float32Array(particleCount);
      
      for (let i = 0; i < particleCount * 3; i += 3) {
        // Create spherical distribution but with some variation and clusters
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(Math.random() * 2 - 1);
        
        // Add some clusters by adding random offsets to radius
        let radius;
        if (Math.random() > 0.3) {
          radius = getRandomFloat(5, 50); // Normal distribution
        } else {
          // Create tighter clusters
          radius = getRandomFloat(10, 25) + (Math.random() > 0.5 ? 15 : 0);
        }
        
        positions[i] = radius * Math.sin(phi) * Math.cos(theta);
        positions[i + 1] = radius * Math.sin(phi) * Math.sin(theta);
        positions[i + 2] = radius * Math.cos(phi);
        
        // Vary particle sizes
        sizes[i/3] = Math.random() > 0.95 ? getRandomFloat(0.1, 0.2) : getRandomFloat(0.04, 0.09);
        
        // Particle coloring
        if (Math.random() > 0.85) {
          // Accent particles (bright)
          colors[i] = 1.0;
          colors[i + 1] = 1.0;
          colors[i + 2] = 1.0;
        } else {
          // Base color particles with subtle variation
          colors[i] = colorScheme.base.r + getRandomFloat(-0.1, 0.1);
          colors[i + 1] = colorScheme.base.g + getRandomFloat(-0.1, 0.1);
          colors[i + 2] = colorScheme.base.b + getRandomFloat(-0.1, 0.1);
        }
      }
      
      geometry = new THREE.BufferGeometry();
      geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
      geometry.setAttribute('color', new THREE.BufferAttribute(colors, 3));
      geometry.setAttribute('size', new THREE.BufferAttribute(sizes, 1));
      
      // Create shader material for better-looking particles
      material = new THREE.PointsMaterial({ 
        size: 0.08, 
        vertexColors: true, 
        transparent: true, 
        opacity: 0.8, 
        sizeAttenuation: true,
        blending: THREE.AdditiveBlending,
      });
      
      particles = new THREE.Points(geometry, material);
      scene.add(particles);
      
      // Remember the particle group
      particleGroups.push(particles);
    };
    
    const addExplosion = (x, y, z) => {
      const explosionParticleCount = 150;
      const explosionGeometry = new THREE.BufferGeometry();
      const explosionPositions = new Float32Array(explosionParticleCount * 3);
      const explosionColors = new Float32Array(explosionParticleCount * 3);
      
      const colorScheme = colorSchemes[activeColorScheme];
      
      // Create burst particles around the click position
      for (let i = 0; i < explosionParticleCount * 3; i += 3) {
        // Randomize in a sphere around the click
        const theta = Math.random() * Math.PI * 2;
        const phi = Math.acos(Math.random() * 2 - 1);
        const radius = getRandomFloat(0.1, 3);
        
        explosionPositions[i] = x + radius * Math.sin(phi) * Math.cos(theta);
        explosionPositions[i + 1] = y + radius * Math.sin(phi) * Math.sin(theta);
        explosionPositions[i + 2] = z + radius * Math.cos(phi);
        
        // Bright accent colors for explosion
        explosionColors[i] = colorScheme.accent.r;
        explosionColors[i + 1] = colorScheme.accent.g;
        explosionColors[i + 2] = colorScheme.accent.b;
      }
      
      explosionGeometry.setAttribute('position', new THREE.BufferAttribute(explosionPositions, 3));
      explosionGeometry.setAttribute('color', new THREE.BufferAttribute(explosionColors, 3));
      
      const explosionMaterial = new THREE.PointsMaterial({
        size: 0.15,
        vertexColors: true,
        transparent: true,
        opacity: 1,
        sizeAttenuation: true,
        blending: THREE.AdditiveBlending,
      });
      
      const explosion = new THREE.Points(explosionGeometry, explosionMaterial);
      scene.add(explosion);
      
      // Animate the explosion
      const startTime = clock.getElapsedTime();
      const explosionDuration = 1.5;
      
      // Store for animation and cleanup
      const explosionData = {
        mesh: explosion,
        geometry: explosionGeometry,
        material: explosionMaterial,
        startTime,
        duration: explosionDuration,
        update: (currentTime) => {
          const age = currentTime - startTime;
          const progress = age / explosionDuration;
          
          if (progress >= 1) {
            // Remove when animation is complete
            scene.remove(explosion);
            explosionGeometry.dispose();
            explosionMaterial.dispose();
            return true; // Mark for removal
          }
          
          // Expand and fade out
          explosion.scale.set(1 + progress * 2, 1 + progress * 2, 1 + progress * 2);
          explosionMaterial.opacity = 1 - progress;
          
          return false;
        }
      };
      
      return explosionData;
    };
    
    // Track active explosions
    let activeExplosions = [];
    
    const changeColorScheme = () => {
      activeColorScheme = (activeColorScheme + 1) % colorSchemes.length;
      createParticleSystem();
      
      // Prevent rapid clicks
      particleClickEnabled = false;
      setTimeout(() => {
        particleClickEnabled = true;
      }, 800);
    };
    
    const animate = () => {
      if (!renderer || !scene || !camera || !particles || !clock) return;
      
      const elapsedTime = clock.getElapsedTime();
      
      // Rotate main particle systems
      particles.rotation.y = elapsedTime * 0.05;
      particles.rotation.x = elapsedTime * 0.02;
      
      // Smooth camera movement
      camera.position.x += (mouse.current.x * 0.7 - camera.position.x) * 0.02;
      camera.position.y += (mouse.current.y * 0.7 - camera.position.y) * 0.02;
      camera.lookAt(scene.position);
      
      // Update explosions
      activeExplosions = activeExplosions.filter(explosion => !explosion.update(elapsedTime));
      
      // Make particles breathe/pulse slightly
      const breatheFactor = (Math.sin(elapsedTime * 0.5) * 0.03) + 1;
      particles.scale.set(breatheFactor, breatheFactor, breatheFactor);
      
      renderer.render(scene, camera);
      animationFrameId = requestAnimationFrame(animate);
    };
    
    // Handle mouse movement for parallax effect
    const handleMouseMove = (event) => {
      if (!currentMount) return;
      const rect = currentMount.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      mouse.current.x = x;
      mouse.current.y = y;
    };
    
    // Handle click for interactive particles
    const handleClick = (event) => {
      if (!particleClickEnabled || !currentMount || !scene || !camera) return;
      
      const currentTime = Date.now();
      if (currentTime - lastInteractionTime < 500) return; // Prevent rapid clicks
      lastInteractionTime = currentTime;
      
      // Get normalized device coordinates
      const rect = currentMount.getBoundingClientRect();
      const x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((event.clientY - rect.top) / rect.height) * 2 + 1;
      
      // Create a ray from the camera to the click point
      const raycaster = new THREE.Raycaster();
      raycaster.params.Points.threshold = 0.5; // Adjust for easier clicking
      
      const mouseVector = new THREE.Vector2(x, y);
      raycaster.setFromCamera(mouseVector, camera);
      
      // Check for particle intersections
      const intersects = raycaster.intersectObject(particles);
      
      if (intersects.length > 0) {
        // Create explosion at the intersection point
        const point = intersects[0].point;
        const explosion = addExplosion(point.x, point.y, point.z);
        activeExplosions.push(explosion);
        
        // Track interactions for color scheme changes
        setInteractionCount(prev => {
          const newCount = prev + 1;
          return newCount;
        });
        
        // Change color scheme every few clicks
        if (interactionCount % 5 === 4) {
          changeColorScheme();
        }
      }
    };
    
    // Handle window resize
    const handleResize = () => {
      if (currentMount && camera && renderer) {
        camera.aspect = currentMount.clientWidth / currentMount.clientHeight;
        camera.updateProjectionMatrix();
        renderer.setSize(currentMount.clientWidth, currentMount.clientHeight);
      }
    };
    
    // Initialize 
    initThree();
    
    // Add event listeners
    currentMount.addEventListener('mousemove', handleMouseMove);
    currentMount.addEventListener('click', handleClick);
    window.addEventListener('resize', handleResize);
    
    // Cleanup function
    return () => {
      window.removeEventListener('resize', handleResize);
      if (currentMount) {
        currentMount.removeEventListener('mousemove', handleMouseMove);
        currentMount.removeEventListener('click', handleClick);
      }
      
      cancelAnimationFrame(animationFrameId);
      
      if (renderer && renderer.domElement && renderer.domElement.parentNode === currentMount) {
        currentMount.removeChild(renderer.domElement);
      }
      
      // Clean up all geometries and materials
      if (geometry) geometry.dispose();
      if (material) material.dispose();
      
      // Clean up explosions
      activeExplosions.forEach(explosion => {
        if (explosion.geometry) explosion.geometry.dispose();
        if (explosion.material) explosion.material.dispose();
      });
      
      // Clear references
      scene = null;
      camera = null;
      renderer = null;
      particles = null;
      geometry = null;
      material = null;
      clock = null;
      activeExplosions = [];
    };
  }, [interactionCount]);

    return (
        <section className="text-white min-h-[calc(100vh_-_theme(space.10)_-_36px)] md:min-h-[calc(100vh_-_theme(space.8)_-_36px)] flex items-center justify-center text-center p-6 relative overflow-hidden bg-black">
      <div ref={mountRef} className="absolute top-0 left-0 w-full h-full z-0 opacity-80 cursor-pointer"></div>
            <div className="max-w-3xl relative z-10 flex flex-col items-center">
                <h1 className="text-4xl md:text-6xl font-bold mb-4 leading-tight tracking-tight">
                    Automate.<span className="text-purple-400"> Amplify.</span> Accelerate.
                </h1>
                <p className="text-lg md:text-xl text-gray-300 mb-8 max-w-xl mx-auto">
                    Metabees AI: Architecting intelligent automation to unlock your business potential.
                </p>
                {/* Single "Get Started" Button */}
                <div
                    onClick={() => handleNavigate('/login')}
                    className="bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 px-8 rounded-md transition duration-300 ease-in-out text-base cursor-pointer inline-block shadow-lg hover:shadow-purple-500/40"
                    role="button"
                    tabIndex={0}
                    onKeyPress={(e) => e.key === 'Enter' && handleNavigate('/login')}
                >
                    Get Started
                </div>
        
                {/* Interactive instructions */}
                <div className="text-xs text-gray-500 mt-6 animate-pulse">
                  Click on particles to interact with the visualization
                </div>
            </div>
        </section>
    );
}

// --- Stats Section - Minimalist Style ---
function StatsSection() {
  const [isAnimated, setIsAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setIsAnimated(true); observer.unobserve(entry.target); } },
      { threshold: 0.2 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => { if (sectionRef.current) observer.disconnect(); };
  }, []);

  const StatItem = ({ value, label, colorClass, delay }) => (
    <div className={`text-center transition-all duration-500 ease-out ${isAnimated ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`} style={{ transitionDelay: `${delay}s` }}>
      <div className={`text-4xl md:text-5xl font-semibold ${colorClass} mb-1 achievement-counter`}>{value}</div>
      <div className="text-sm text-gray-400 tracking-wide">{label}</div>
    </div>
  );

  return (
    <section ref={sectionRef} className="bg-black py-16 md:py-20 border-t border-b border-gray-800/50">
      <div className="max-w-5xl mx-auto px-6 text-center">
        <h3 className={`text-base text-gray-400 mb-8 md:mb-10 transition-opacity duration-700 ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
          Trusted by <span className="font-medium text-purple-400">150+ global enterprises</span> daily.
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-10 md:gap-12">
          <StatItem value="5M+" label="Emails Automated" colorClass="text-purple-400" delay={0.1} />
          <StatItem value="1,000" label="Business Use Cases" colorClass="text-gray-300" delay={0.25} />
          <StatItem value="50" label="API Platforms" colorClass="text-purple-400" delay={0.4} />
        </div>
      </div>
    </section>
  );
}

// --- Echidna Foundry Section - Minimalist Style ---
function EchidnaFoundrySection() {
    const [isAnimated, setIsAnimated] = useState(false); const sectionRef = useRef(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsAnimated(true); observer.unobserve(entry.target); } },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.disconnect(); };
    }, []);

    const ECHIDNA_FOUNDRY_LINK = "https://www.metabees.org/echidna-ai-foundry";

    return (
      <section ref={sectionRef} className="bg-black text-gray-300 py-20 md:py-28 px-6 md:px-8 border-b border-gray-800/50 relative overflow-hidden">
         <div className="absolute inset-0 z-0 opacity-[0.02]" style={{ backgroundImage: `radial-gradient(#a855f7 1px, transparent 1px)`, backgroundSize: '35px 35px' }}></div>
         <div className="absolute inset-0 -z-10 bg-black"></div>

        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16 relative z-10">
          {/* SVG Visualizer */}
          <div className={`w-full md:w-1/2 flex justify-center items-center aspect-square md:aspect-auto min-h-[300px] md:min-h-[400px] transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '0.3s'}}>
            <svg viewBox="0 0 400 400" className="w-full h-full max-w-sm md:max-w-[360px]" preserveAspectRatio="xMidYMid meet">
              <defs>
                <radialGradient id="coreGradientFoundryMin" cx="50%" cy="50%" r="60%" fx="50%" fy="50%">
                  <stop offset="0%" stopColor="#c084fc" stopOpacity="0.6"/>
                  <stop offset="100%" stopColor="#7e22ce" stopOpacity="0.1"/>
                </radialGradient>
                 <filter id="glowFoundryMin" x="-100%" y="-100%" width="300%" height="300%">
                  <feGaussianBlur stdDeviation="6" result="coloredBlur" />
                  <feMerge> <feMergeNode in="coloredBlur"/> <feMergeNode in="SourceGraphic"/> </feMerge>
                </filter>
              </defs>
              <g className={`core ${isAnimated ? 'animate-core-enter-subtle' : ''}`}>
                 <circle cx="200" cy="200" r="60" fill="url(#coreGradientFoundryMin)" filter="url(#glowFoundryMin)" />
                 <circle cx="200" cy="200" r="58" fill="none" stroke="#e9d5ff" strokeWidth="0.75" strokeDasharray="4 8" opacity="0.5" className="core-spinner-subtle"/>
                 <circle cx="200" cy="200" r="40" fill="none" stroke="#c084fc" strokeWidth="0.5" opacity="0.3" />
              </g>
              <g className={`stream ${isAnimated ? 'animate-stream-enter-subtle' : ''}`} style={{ animationDelay: `${GLOBAL_CORE_APPEAR_DURATION * 0.6}s` }}>
                <path id="foundryStreamPath1Min" d="M 80 80 Q 150 120, 185 180" stroke="#a855f7" strokeWidth="1" fill="none" className="animated-stream-path-subtle" />
                <circle cx="80" cy="80" r="4" fill="#a855f7" opacity="0.7" />
                <circle r="2.5" fill="#e9d5ff" className="pulse-subtle">
                  <animateMotion dur={`${GLOBAL_PULSE_DURATION*1.2}s`} repeatCount="indefinite" begin={`${GLOBAL_CORE_APPEAR_DURATION + GLOBAL_STREAM_GROW_DURATION}s`}> <mpath xlinkHref="#foundryStreamPath1Min"/> </animateMotion>
                  <animate attributeName="opacity" values="0;.8;0" dur={`${GLOBAL_PULSE_DURATION*1.2}s`} repeatCount="indefinite" begin={`${GLOBAL_CORE_APPEAR_DURATION + GLOBAL_STREAM_GROW_DURATION}s`} />
                </circle>
                <path id="foundryStreamPath2Min" d="M 80 320 Q 130 280, 180 220" stroke="#a855f7" strokeWidth="1" fill="none" className="animated-stream-path-subtle" style={{ animationDelay: `${GLOBAL_STREAM_GROW_DURATION * 0.2}s` }}/>
                <circle cx="80" cy="320" r="4" fill="#a855f7" opacity="0.7" />
                <circle r="2.5" fill="#e9d5ff" className="pulse-subtle">
                   <animateMotion dur={`${GLOBAL_PULSE_DURATION*1.2}s`} repeatCount="indefinite" begin={`${GLOBAL_CORE_APPEAR_DURATION + GLOBAL_STREAM_GROW_DURATION + 0.4}s`}> <mpath xlinkHref="#foundryStreamPath2Min"/> </animateMotion>
                   <animate attributeName="opacity" values="0;.8;0" dur={`${GLOBAL_PULSE_DURATION*1.2}s`} repeatCount="indefinite" begin={`${GLOBAL_CORE_APPEAR_DURATION + GLOBAL_STREAM_GROW_DURATION + 0.4}s`} />
                </circle>
                <path id="foundryStreamPath3Min" d="M 320 200 Q 260 150, 220 180" stroke="#a855f7" strokeWidth="1" fill="none" className="animated-stream-path-subtle" style={{ animationDelay: `${GLOBAL_STREAM_GROW_DURATION * 0.4}s` }}/>
                 <rect x="316" y="196" width="8" height="8" rx="2" fill="#a855f7" opacity="0.7" />
                 <circle r="2.5" fill="#e9d5ff" className="pulse-subtle">
                   <animateMotion dur={`${GLOBAL_PULSE_DURATION*1.2}s`} repeatCount="indefinite" begin={`${GLOBAL_CORE_APPEAR_DURATION + GLOBAL_STREAM_GROW_DURATION + 0.8}s`}> <mpath xlinkHref="#foundryStreamPath3Min"/> </animateMotion>
                   <animate attributeName="opacity" values="0;.8;0" dur={`${GLOBAL_PULSE_DURATION*1.2}s`} repeatCount="indefinite" begin={`${GLOBAL_CORE_APPEAR_DURATION + GLOBAL_STREAM_GROW_DURATION + 0.8}s`} />
                </circle>
              </g>
            </svg>
          </div>
          {/* Text Content */}
          <div className={`w-full md:w-1/2 text-center md:text-left transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
             <h2 className="text-3xl md:text-4xl font-medium text-gray-100 mb-4 tracking-tight"> Metabees <span className="text-purple-400">Echidna Foundry</span> </h2>
             <p className="text-base md:text-lg text-gray-400 mb-8 max-w-lg mx-auto md:mx-0 leading-relaxed">
                Your dedicated platform to design, train, and deploy bespoke Large Language Models. Build custom AI tailored precisely to your business needs and data.
             </p>
             <div className="space-y-5 mb-10 text-left max-w-md mx-auto md:mx-0">
                {/* Feature Items */}
                <div className="flex items-start gap-3 group">
                    <div className="w-px h-4 bg-purple-600/50 mt-1 group-hover:bg-purple-400 transition-colors"></div>
                    <div>
                        <h4 className="font-medium text-gray-200 text-sm mb-0.5">Custom Model Training</h4>
                        <p className="text-xs text-gray-500 leading-snug">Fine-tune base models or train from scratch using your proprietary data.</p>
                    </div>
                </div>
                 <div className="flex items-start gap-3 group">
                     <div className="w-px h-4 bg-purple-600/50 mt-1 group-hover:bg-purple-400 transition-colors"></div>
                    <div>
                        <h4 className="font-medium text-gray-200 text-sm mb-0.5">Secure Data Handling</h4>
                        <p className="text-xs text-gray-500 leading-snug">Enterprise-grade security for your sensitive training datasets.</p>
                    </div>
                </div>
                 <div className="flex items-start gap-3 group">
                     <div className="w-px h-4 bg-purple-600/50 mt-1 group-hover:bg-purple-400 transition-colors"></div>
                    <div>
                        <h4 className="font-medium text-gray-200 text-sm mb-0.5">Seamless Deployment API</h4>
                        <p className="text-xs text-gray-500 leading-snug">Integrate your custom LLMs easily into applications and workflows.</p>
                    </div>
                </div>
             </div>
             {/* Clickable Div */}
             <div
                onClick={() => handleNavigate(ECHIDNA_FOUNDRY_LINK)}
                className="border border-purple-600 hover:border-purple-400 text-purple-300 hover:text-white font-medium py-2.5 px-6 rounded-md transition duration-300 ease-in-out text-base inline-block cursor-pointer group"
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleNavigate(ECHIDNA_FOUNDRY_LINK)}
             >
                 Explore Foundry <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-1">→</span>
             </div>
          </div>
        </div>
      </section>
    );
}


// --- Anne SDR Section - Minimalist Style (with Image) ---
function AnneSDRSection() {
    const [isAnimated, setIsAnimated] = useState(false); const sectionRef = useRef(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsAnimated(true); observer.unobserve(entry.target); } },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.disconnect(); };
    }, []);

    const ANNE_SDR_LINK = "https://www.metabees.org/anne";
    const ANNE_IMAGE_URL = "https://cybverlake-test.s3.ap-south-1.amazonaws.com/ChatGPT_Image_Apr_26__2025__11_39_08_AM-removebg-preview.png";

    return (
      <section id="anne-sdr" ref={sectionRef} className="bg-black text-gray-300 py-20 md:py-28 px-6 md:px-8 border-b border-gray-800/50 relative overflow-hidden">
         <div className="absolute inset-0 z-0 opacity-[0.02]" style={{ backgroundImage: `radial-gradient(#06b6d4 1px, transparent 1px)`, backgroundSize: '35px 35px' }}></div>
         <div className="absolute inset-0 -z-10 bg-black"></div>

        <div className="max-w-6xl mx-auto flex flex-col md:flex-row-reverse items-center gap-12 md:gap-16 relative z-10">
          {/* Animated SVG Visualization */}
          <div className={`w-full md:w-1/2 flex justify-center items-center transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '0.3s'}}>
             <svg viewBox="0 0 400 400" className="w-full h-full max-w-xs md:max-w-sm" preserveAspectRatio="xMidYMid meet">
                <defs>
                    <linearGradient id="platformGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#0b5cad" stopOpacity="0.9"/>
                        <stop offset="100%" stopColor="#0891b2" stopOpacity="0.3"/>
                    </linearGradient>
                    <linearGradient id="headerGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#0f2b46" stopOpacity="1"/>
                        <stop offset="100%" stopColor="#164063" stopOpacity="1"/>
                    </linearGradient>
                    <linearGradient id="cardGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#193654" stopOpacity="1"/>
                        <stop offset="100%" stopColor="#0f2b46" stopOpacity="1"/>
                    </linearGradient>
                    <linearGradient id="chartGrad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#22d3ee" stopOpacity="0.4"/>
                        <stop offset="100%" stopColor="#22d3ee" stopOpacity="0.01"/>
                    </linearGradient>
                    <filter id="platformGlow" x="-100%" y="-100%" width="300%" height="300%">
                        <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
                        <feMerge>
                            <feMergeNode in="coloredBlur"/>
                            <feMergeNode in="SourceGraphic"/>
                        </feMerge>
                    </filter>
                    <symbol id="icon-dashboard" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zm0 8a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zm12-1a1 1 0 00-1 1v6a1 1 0 001 1h2a1 1 0 001-1v-6a1 1 0 00-1-1h-2z" />
                    </symbol>
                    <symbol id="icon-analytics" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"/>
                    </symbol>
                    <symbol id="icon-automation" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z"/>
                    </symbol>
                    <symbol id="icon-settings" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    </symbol>
                    <clipPath id="graphClip">
                        <rect x="80" y="200" width="260" height="100" rx="4" />
                    </clipPath>
                </defs>
                
                {/* Platform Base */}
                <g className={`platform-base ${isAnimated ? 'animate-platform-enter' : ''}`}>
                    <rect x="40" y="40" width="320" height="320" rx="5" fill="#0a1929" stroke="#22d3ee" strokeWidth="0.5" opacity="0.9"/>
                </g>

                {/* Side Navigation */}
                <g className="side-nav">
                    <rect x="40" y="40" width="35" height="320" fill="#071420" />
                    <rect x="40" y="40" width="35" height="50" fill="#061b2d" />
                    <circle cx="57.5" cy="65" r="10" fill="#22d3ee" fillOpacity="0.2" stroke="#22d3ee" strokeWidth="0.5" />
                    <text x="57.5" y="68" textAnchor="middle" fontSize="10" fill="#e0f2fe" fontWeight="bold">SDR</text>
                    
                    <rect x="47.5" y="105" width="20" height="20" rx="3" fill="#22d3ee" fillOpacity="0.2" />
                    <use xlinkHref="#icon-dashboard" x="50.5" y="108" width="14" height="14" fill="#22d3ee" opacity="0.8"/>
                    
                    <rect x="47.5" y="140" width="20" height="20" rx="3" fill="#164063" />
                    <use xlinkHref="#icon-analytics" x="50.5" y="143" width="14" height="14" fill="#64748b" opacity="0.8"/>
                    
                    <rect x="47.5" y="175" width="20" height="20" rx="3" fill="#164063" />
                    <use xlinkHref="#icon-automation" x="50.5" y="178" width="14" height="14" fill="#64748b" opacity="0.8"/>
                    
                    <rect x="47.5" y="320" width="20" height="20" rx="3" fill="#164063" />
                    <use xlinkHref="#icon-settings" x="50.5" y="323" width="14" height="14" fill="#64748b" opacity="0.8"/>
                </g>

                {/* Dashboard Elements */}
                <g className={`platform-elements ${isAnimated ? 'animate-platform-elements-enter' : ''}`}>
                    {/* Header Bar */}
                    <rect x="75" y="40" width="285" height="50" fill="url(#headerGrad)"/>
                    <text x="95" y="65" fill="#e0f2fe" fontSize="14" fontWeight="600">Anne SDR Platform</text>
                    <text x="290" y="65" fill="#64748b" fontSize="10">v2.4.1</text>
                    <circle cx="320" cy="65" r="8" fill="#164063" stroke="#64748b" strokeWidth="0.5" />
                    <text x="320" y="68" textAnchor="middle" fontSize="8" fill="#e0f2fe">MO</text>
                    <circle cx="345" cy="65" r="3" fill="#22d3ee" opacity="0.8"/>
                    
                    {/* Search Bar */}
                    <rect x="85" y="100" width="210" height="25" rx="4" fill="#071420" stroke="#164063" strokeWidth="0.5"/>
                    <text x="105" y="117" fill="#64748b" fontSize="10">Search resources, agents, metrics...</text>
                    <rect x="305" y="100" width="45" height="25" rx="4" fill="#22d3ee" fillOpacity="0.1"/>
                    <text x="327.5" y="117" textAnchor="middle" fill="#22d3ee" fontSize="10">Filter</text>
                    
                    {/* Tabs */}
                    <rect x="85" y="135" width="75" height="25" rx="4" fill="#22d3ee" fillOpacity="0.1" stroke="#22d3ee" strokeWidth="0.5"/>
                    <text x="122.5" y="152" textAnchor="middle" fill="#e0f2fe" fontSize="10">Dashboard</text>
                    
                    <rect x="165" y="135" width="75" height="25" rx="4" fill="#071420"/>
                    <text x="202.5" y="152" textAnchor="middle" fill="#64748b" fontSize="10">Campaigns</text>
                    
                    <rect x="245" y="135" width="75" height="25" rx="4" fill="#071420"/>
                    <text x="282.5" y="152" textAnchor="middle" fill="#64748b" fontSize="10">Analytics</text>
                    
                    {/* Status Banner */}
                    <rect x="85" y="170" width="265" height="20" rx="2" fill="#0c4a6e" fillOpacity="0.3"/>
                    <text x="95" y="184" fill="#7dd3fc" fontSize="9">Service Health: All Systems Operational | Last Synced: 4m ago</text>
                    <circle cx="335" cy="180" r="4" fill="#0ea5e9"/>
                    
                    {/* Top Stats Row */}
                    <g className="stats-cards">
                        <rect x="85" y="200" width="80" height="80" rx="4" fill="url(#cardGrad)" className="platform-card" stroke="#164063" strokeWidth="0.5"/>
                        <text x="95" y="220" fill="#64748b" fontSize="9">BUSINESS USE CASES</text>
                        <text x="95" y="240" fill="#e0f2fe" fontSize="18" fontWeight="600">1,000</text>
                        <text x="95" y="260" fill="#10b981" fontSize="9">▲ 12.8%</text>
                        <path d="M100 272 L120 264 L140 268 L160 260" stroke="#10b981" strokeWidth="1" fill="none" opacity="0.7"/>
                        
                        <rect x="175" y="200" width="80" height="80" rx="4" fill="url(#cardGrad)" className="platform-card" stroke="#164063" strokeWidth="0.5"/>
                        <text x="185" y="220" fill="#64748b" fontSize="9">API PLATFORMS</text>
                        <text x="185" y="240" fill="#e0f2fe" fontSize="18" fontWeight="600">50</text>
                        <text x="185" y="260" fill="#10b981" fontSize="9">▲ 5.2%</text>
                        <path d="M190 272 L210 265 L230 270 L250 260" stroke="#10b981" strokeWidth="1" fill="none" opacity="0.7"/>
                        
                        <rect x="265" y="200" width="80" height="80" rx="4" fill="url(#cardGrad)" className="platform-card" stroke="#164063" strokeWidth="0.5"/>
                        <text x="275" y="220" fill="#64748b" fontSize="9">RESPONSE RATE</text>
                        <text x="275" y="240" fill="#e0f2fe" fontSize="18" fontWeight="600">78.5%</text>
                        <text x="275" y="260" fill="#f59e0b" fontSize="9">► 0.3%</text>
                        <path d="M280 272 L300 271 L320 268 L340 267" stroke="#f59e0b" strokeWidth="1" fill="none" opacity="0.7"/>
                    </g>

                    {/* Main Graph */}
                    <rect x="85" y="290" width="175" height="110" rx="4" fill="url(#cardGrad)" className="platform-card" stroke="#164063" strokeWidth="0.5"/>
                    <text x="95" y="310" fill="#64748b" fontSize="9">OUTREACH PERFORMANCE</text>
                    <text x="235" y="310" textAnchor="end" fill="#64748b" fontSize="8">LAST 30 DAYS</text>
                    
                    {/* Line Chart */}
                    <g transform="translate(0, 10)">
                        <line x1="95" y1="350" x2="250" y2="350" stroke="#164063" strokeWidth="0.5" strokeDasharray="1 3"/>
                        <line x1="95" y1="330" x2="250" y2="330" stroke="#164063" strokeWidth="0.5" strokeDasharray="1 3"/>
                        <line x1="95" y1="370" x2="250" y2="370" stroke="#164063" strokeWidth="0.5" strokeDasharray="1 3"/>
                        
                        <text x="92" y="350" textAnchor="end" fill="#64748b" fontSize="6">50</text>
                        <text x="92" y="330" textAnchor="end" fill="#64748b" fontSize="6">100</text>
                        <text x="92" y="370" textAnchor="end" fill="#64748b" fontSize="6">0</text>
                        
                        <path d="M95 350 L115 345 L135 340 L155 330 L175 335 L195 325 L215 320 L235 325 L250 315" 
                              stroke="#0ea5e9" strokeWidth="1.5" fill="none" className="activity-line"/>
                        <path d="M95 360 L115 365 L135 370 L155 360 L175 355 L195 360 L215 365 L235 355 L250 360" 
                              stroke="#22d3ee" strokeWidth="1.5" fill="none" className="activity-line"/>
                              
                        <path d="M95 350 L115 345 L135 340 L155 330 L175 335 L195 325 L215 320 L235 325 L250 315 L250 370 L95 370 Z" 
                              fill="url(#chartGrad)" opacity="0.3"/>
                              
                        {/* Data Points */}
                        <circle cx="115" cy="345" r="2" fill="#0ea5e9" opacity="0.9"/>
                        <circle cx="155" cy="330" r="2" fill="#0ea5e9" opacity="0.9"/>
                        <circle cx="195" cy="325" r="2" fill="#0ea5e9" opacity="0.9"/>
                        <circle cx="250" cy="315" r="2" fill="#0ea5e9" opacity="0.9"/>
                        
                        <circle cx="115" cy="365" r="2" fill="#22d3ee" opacity="0.9"/>
                        <circle cx="155" cy="360" r="2" fill="#22d3ee" opacity="0.9"/>
                        <circle cx="195" cy="360" r="2" fill="#22d3ee" opacity="0.9"/>
                        <circle cx="250" cy="360" r="2" fill="#22d3ee" opacity="0.9"/>
                        
                        {/* Legend */}
                        <circle cx="110" cy="385" r="2" fill="#0ea5e9"/>
                        <text x="115" y="387" fill="#64748b" fontSize="7">Emails</text>
                        <circle cx="140" cy="385" r="2" fill="#22d3ee"/>
                        <text x="145" y="387" fill="#64748b" fontSize="7">Replies</text>
                    </g>
                    
                    {/* Right Side Widgets */}
                    <rect x="270" y="290" width="75" height="50" rx="4" fill="url(#cardGrad)" className="platform-card" stroke="#164063" strokeWidth="0.5"/>
                    <text x="280" y="310" fill="#64748b" fontSize="9">AGENT HEALTH</text>
                    <text x="280" y="330" fill="#e0f2fe" fontSize="18" fontWeight="600">98.2%</text>
                    
                    <rect x="270" y="350" width="75" height="50" rx="4" fill="url(#cardGrad)" className="platform-card" stroke="#164063" strokeWidth="0.5"/>
                    <text x="280" y="370" fill="#64748b" fontSize="9">ACTIVE AGENTS</text>
                    <text x="280" y="390" fill="#e0f2fe" fontSize="18" fontWeight="600">12/15</text>
                    
                    {/* Resource Monitor */}
                    <rect x="85" y="410" width="260" height="35" rx="2" fill="#0f172a" stroke="#164063" strokeWidth="0.5"/>
                    <rect x="95" y="425" width="140" height="4" rx="2" fill="#071420"/>
                    <rect x="95" y="425" width="80" height="4" rx="2" fill="#0ea5e9"/>
                    <text x="95" y="422" fill="#64748b" fontSize="8">CPU: 45%</text>
                    
                    <rect x="245" y="425" width="90" height="4" rx="2" fill="#071420"/>
                    <rect x="245" y="425" width="65" height="4" rx="2" fill="#22d3ee"/>
                    <text x="245" y="422" fill="#64748b" fontSize="8">Memory: 3.1GB/4GB</text>
                </g>

                {/* Data Flow Lines */}
                <g className={`platform-data-flow ${isAnimated ? 'animate-platform-data-flow-enter' : ''}`}>
                    <path d="M125 280 Q 125 320, 125 350" stroke="#0ea5e9" strokeWidth="0.5" fill="none" opacity="0.3" strokeDasharray="3,2" className="data-flow-path">
                        <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2s" repeatCount="indefinite"/>
                    </path>
                    <path d="M215 280 Q 215 320, 215 350" stroke="#0ea5e9" strokeWidth="0.5" fill="none" opacity="0.3" strokeDasharray="3,2" className="data-flow-path">
                        <animate attributeName="stroke-dashoffset" from="100" to="0" dur="2.5s" repeatCount="indefinite"/>
                    </path>
                    <path d="M305 280 Q 305 330, 280 350" stroke="#0ea5e9" strokeWidth="0.5" fill="none" opacity="0.3" strokeDasharray="3,2" className="data-flow-path">
                        <animate attributeName="stroke-dashoffset" from="100" to="0" dur="3s" repeatCount="indefinite"/>
                    </path>
                </g>
             </svg>
          </div>
          {/* Text Content */}
          <div className={`w-full md:w-1/2 text-center md:text-left transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
             <h2 className="text-3xl md:text-4xl font-medium text-gray-100 mb-4 tracking-tight"> AI-Driven Sales with <span className="text-cyan-400">Anne SDR</span> </h2>
             <p className="text-base md:text-lg text-gray-400 mb-8 max-w-lg mx-auto md:mx-0 leading-relaxed">
               Empower your sales team with an autonomous AI agent. Anne handles prospecting, personalized outreach, and meeting scheduling, letting your team focus on closing.
             </p>
             <div className="space-y-5 mb-10 text-left max-w-md mx-auto md:mx-0">
                 {/* Feature Items */}
                 <div className="flex items-start gap-3 group">
                    <div className="w-px h-4 bg-cyan-600/50 mt-1 group-hover:bg-cyan-400 transition-colors"></div>
                    <div>
                        <h4 className="font-medium text-gray-200 text-sm mb-0.5">Targeted Prospecting Engine</h4>
                        <p className="text-xs text-gray-500 leading-snug">Identify and qualify high-potential leads based on your ideal customer profile.</p>
                    </div>
                </div>
                 <div className="flex items-start gap-3 group">
                     <div className="w-px h-4 bg-cyan-600/50 mt-1 group-hover:bg-cyan-400 transition-colors"></div>
                    <div>
                        <h4 className="font-medium text-gray-200 text-sm mb-0.5">Hyper-Personalized Outreach</h4>
                        <p className="text-xs text-gray-500 leading-snug">Generate context-aware communication that resonates with prospects.</p>
                    </div>
                </div>
                 <div className="flex items-start gap-3 group">
                     <div className="w-px h-4 bg-cyan-600/50 mt-1 group-hover:bg-cyan-400 transition-colors"></div>
                    <div>
                        <h4 className="font-medium text-gray-200 text-sm mb-0.5">Intelligent Meeting Scheduling</h4>
                        <p className="text-xs text-gray-500 leading-snug">Automate booking qualified appointments directly into sales calendars.</p>
                    </div>
                </div>
             </div>
             {/* Clickable Div */}
             <div
                onClick={() => handleNavigate(ANNE_SDR_LINK)}
                 className="border border-cyan-600 hover:border-cyan-400 text-cyan-300 hover:text-white font-medium py-2.5 px-6 rounded-md transition duration-300 ease-in-out text-base inline-block cursor-pointer group"
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleNavigate(ANNE_SDR_LINK)}
              >
                 Explore Anne SDR <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-1">→</span>
             </div>
          </div>
        </div>
      </section>
    );
}

// --- Cost Comparison Slider Component ---
const CostComparisonSlider = ({ aiCost = 1000, fteCost = 5000, accentColor = 'text-purple-400', isVisible }) => {
    const savings = fteCost - aiCost;
    const savingsPercentage = fteCost > 0 ? Math.round((savings / fteCost) * 100) : 0;
    const bgColorClass = accentColor.replace('text-', 'bg-').replace('-400', '-600');

    return (
        <div className={`cost-comparison-slider mt-8 pt-6 border-t border-gray-800/50 transition-opacity duration-500 ease-out ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
            <h6 className={`text-xs ${'text-gray-500'} uppercase tracking-wider mb-4`}>Illustrative Cost Comparison (Monthly)</h6>
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    <span className={`w-28 text-xs font-medium ${accentColor}`}>AI Solution:</span>
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className={`h-full ${bgColorClass} rounded-full`} style={{ width: fteCost > 0 ? `${(aiCost / fteCost) * 100}%` : '0%' }}></div>
                    </div>
                    <span className="w-20 text-right text-xs font-semibold text-gray-300">${aiCost.toLocaleString()}/mo</span>
                </div>
                <div className="flex items-center gap-3">
                    <span className="w-28 text-xs font-medium text-gray-400">Typical FTE*:</span>
                    <div className="flex-1 h-2 bg-gray-800 rounded-full overflow-hidden">
                        <div className="h-full bg-gray-600 rounded-full" style={{ width: '100%' }}></div>
                    </div>
                    <span className="w-20 text-right text-xs font-semibold text-gray-300">${fteCost.toLocaleString()}/mo</span>
                </div>
            </div>
            <p className={`text-sm ${accentColor} font-medium text-center mt-5`}>
                Potential Savings: ~${savings.toLocaleString()}/mo ({savingsPercentage}%)
            </p>
             <p className="text-center text-xs text-gray-600 mt-1">*Full-Time Equivalent cost is an estimate for illustration.</p>
        </div>
    );
};

// --- TECHNOLOGY PLATFORM SECTION (FLOWCHART + COST SLIDER + DETAILS REVAMP) ---
function TechnologyPlatformSection() {
    const [isAnimated, setIsAnimated] = useState(false);
    const [activeCategory, setActiveCategory] = useState(null);
    const sectionRef = useRef(null);

    // Theme Colors
    const ACCENT_COLOR_TEXT = 'text-purple-400';
    const ACCENT_COLOR_BORDER = 'border-purple-600/30';
    const ACCENT_COLOR_BORDER_ACTIVE = 'border-purple-400';
    const ACCENT_COLOR_BORDER_HOVER = 'hover:border-purple-500/60';
    const BASE_TEXT_COLOR = 'text-gray-400';
    const HEADING_COLOR = 'text-gray-100';
    const SUBTLE_TEXT_COLOR = 'text-gray-600';
    const INTERACTIVE_BG_HOVER = 'hover:bg-gray-950';
    const ACTIVE_BG = 'bg-black';
    const SVG_LINE_COLOR = '#a855f7';

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsAnimated(true);
                    observer.unobserve(entry.target);
                }
            }, { threshold: 0.1 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.disconnect(); };
    }, []);

    // Expanded Categories List (12 Use Cases)
    const automationCategories = [
        {
            id: "sales-acceleration", name: "Sales Acceleration", fteCostEstimate: 6000,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 18L9 11.25l4.306 4.307a11.95 11.95 0 015.814-5.519l2.74-1.22m0 0l-5.94-2.28a11.95 11.95 0 00-5.814 5.519l-4.475 4.475M3 18.75l-1.5-1.5m1.5 1.5V13.5m0 5.25v-1.5m13.5-9l1.5-1.5m-1.5 1.5V6.75m0 5.25v-1.5m-1.5 13.5l-1.5-1.5m1.5 1.5v-5.25m0 5.25h-1.5m-13.5-1.5l-1.5-1.5M3 18.75v-1.5m0 1.5h1.5" />,
            description: "Boost sales team performance by automating lead identification, scoring, personalized outreach, and meeting scheduling.",
            workflowHighlights: [
                { id: 1, name: "Identify Leads", aiAction: "Analyzes web/CRM data" },
                { id: 2, name: "Score & Prioritize", aiAction: "Uses ML fit models" },
                { id: 3, name: "Personalize Outreach", aiAction: "Generates tailored emails" },
                { id: 4, name: "Automate Follow-ups", aiAction: "Schedules sequences" },
                { id: 5, name: "Schedule Meetings", aiAction: "Syncs calendars via link" }
            ]
        },
        {
            id: "marketing-automation", name: "Marketing Automation", fteCostEstimate: 5500,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 6h9.75M10.5 6a1.5 1.5 0 11-3 0m3 0a1.5 1.5 0 10-3 0M3.75 6H7.5m3 12h9.75m-9.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-3.75 0H7.5m9-6h3.75m-3.75 0a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m-9.75 0h9.75" />,
            description: "Optimize campaigns, generate targeted content, personalize user experiences, and track performance across all channels.",
            workflowHighlights: [
                { id: 1, name: "Segment Audience", aiAction: "Clusters user behavior" },
                { id: 2, name: "Generate Content", aiAction: "Drafts ad copy/posts" },
                { id: 3, name: "Orchestrate Campaigns", aiAction: "Manages multi-channel flows" },
                { id: 4, name: "Analyze Performance", aiAction: "Identifies trends & ROI" },
                { id: 5, name: "Personalize Experience", aiAction: "Adapts web/email content" }
             ]
        },
        {
            id: "customer-service", name: "Customer Service AI", fteCostEstimate: 4500,
             icon: <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 8.25h9m-9 3H12m-9.75 1.51c0 1.6 1.123 2.994 2.707 3.227 1.129.166 2.27.293 3.423.379.35.026.67.21.865.501L12 21l2.755-2.131c.195-.29.515-.475.865-.501 1.153-.086 2.294-.213 3.423-.379 1.584-.233 2.707-1.626 2.707-3.228V6.741c0-1.602-1.123-2.995-2.707-3.228A48.394 48.394 0 0012 3c-2.392 0-4.744.175-7.043.513C3.373 3.746 2.25 5.14 2.25 6.741v6.018z" />,
            description: "Enhance support efficiency with AI-powered ticket routing, 24/7 chatbots, and automated resolution suggestions.",
            workflowHighlights: [
                { id: 1, name: "Route Inquiries", aiAction: "Categorizes support tickets" },
                { id: 2, name: "Deploy Chatbots", aiAction: "Answers common FAQs" },
                { id: 3, name: "Provide Agent Assist", aiAction: "Suggests knowledge articles" },
                { id: 4, name: "Analyze Sentiment", aiAction: "Gauges customer emotion" },
                { id: 5, name: "Track Resolution", aiAction: "Monitors case outcomes" }
            ]
        },
        {
            id: "hr-talent", name: "HR & Talent Mgmt", fteCostEstimate: 5000,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />,
            description: "Automate candidate screening, onboarding processes, performance reviews, and identify development opportunities.",
            workflowHighlights: [
                { id: 1, name: "Screen Candidates", aiAction: "Matches skills to job reqs" },
                { id: 2, name: "Automate Onboarding", aiAction: "Assigns tasks & tracks docs" },
                { id: 3, name: "Track Performance", aiAction: "Aggregates review feedback" },
                { id: 4, name: "Suggest Learning", aiAction: "Recommends training courses" },
                { id: 5, name: "Analyze Retention", aiAction: "Predicts employee flight risk" }
            ]
        },
        {
            id: "financial-ops", name: "Financial Operations", fteCostEstimate: 6500,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />,
            description: "Streamline invoice processing, expense management, fraud detection, financial reporting, and compliance checks.",
            workflowHighlights: [
                { id: 1, name: "Process Invoices", aiAction: "Extracts & validates data" },
                { id: 2, name: "Detect Anomalies", aiAction: "Flags unusual transactions" },
                { id: 3, name: "Generate Reports", aiAction: "Automates P&L / BS creation" },
                { id: 4, name: "Model Forecasts", aiAction: "Predicts cash flow trends" },
                { id: 5, name: "Monitor Compliance", aiAction: "Checks against fin. rules" }
             ]
        },
        {
            id: "supply-chain-opt", name: "Supply Chain Opt.", fteCostEstimate: 5800,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" />,
            description: "Optimize demand forecasting, inventory management, logistics coordination, and supplier relationship monitoring.",
            workflowHighlights: [
                { id: 1, name: "Forecast Demand", aiAction: "Predicts sales & seasonality" },
                { id: 2, name: "Optimize Inventory", aiAction: "Recommends stock levels" },
                { id: 3, name: "Manage Suppliers", aiAction: "Tracks vendor performance" },
                { id: 4, name: "Coordinate Logistics", aiAction: "Optimizes shipping routes" },
                { id: 5, name: "Ensure Quality", aiAction: "Automates QC checks" }
            ]
        },
        {
             id: "it-ops", name: "IT Operations", fteCostEstimate: 7000,
             icon: <path strokeLinecap="round" strokeLinejoin="round" d="M17.5 14H15M17.5 14a2.5 2.5 0 100-5M17.5 14v4.5A2.5 2.5 0 0115 21h-3.5m0 0h-5A2.5 2.5 0 014 18.5V7.154a2.5 2.5 0 011.199-2.13l5.5-3.3a2.5 2.5 0 012.602 0l5.5 3.3A2.5 2.5 0 0120 7.154V11m-7.5-5.5a2.5 2.5 0 100-5 2.5 2.5 0 000 5z" />,
             description: "Automate incident response, infrastructure monitoring, patch management, and user access provisioning for stable systems.",
             workflowHighlights: [
                 { id: 1, name: "Monitor Systems", aiAction: "Tracks health & performance" },
                 { id: 2, name: "Detect Incidents", aiAction: "Identifies anomalies & alerts" },
                 { id: 3, name: "Automate Response", aiAction: "Triggers remediation runbooks" },
                 { id: 4, name: "Manage Patches", aiAction: "Schedules & deploys updates" },
                 { id: 5, name: "Provision Access", aiAction: "Handles user account requests" }
              ]
        },
         {
            id: "data-analysis", name: "Data Analysis", fteCostEstimate: 7500,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h16M4 18h16" />,
            description: "Automate data collection, cleaning, transformation, analysis, and visualization to uncover insights faster.",
            workflowHighlights: [
                { id: 1, name: "Collect Data", aiAction: "Integrates disparate sources" },
                { id: 2, name: "Clean & Prep", aiAction: "Formats, validates, handles missing vals" },
                { id: 3, name: "Run Analysis", aiAction: "Applies statistical models" },
                { id: 4, name: "Generate Reports", aiAction: "Summarizes key findings" },
                { id: 5, name: "Visualize Insights", aiAction: "Creates interactive charts" }
             ]
        },
        {
            id: "compliance-risk", name: "Compliance & Risk", fteCostEstimate: 6800,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />,
            description: "Automate regulatory monitoring, policy enforcement checks, risk assessments, and audit trail generation.",
            workflowHighlights: [
                { id: 1, name: "Monitor Regulations", aiAction: "Tracks regulatory changes" },
                { id: 2, name: "Assess Risks", aiAction: "Identifies potential violations" },
                { id: 3, name: "Enforce Policies", aiAction: "Checks internal adherence" },
                { id: 4, name: "Generate Audit Logs", aiAction: "Records compliance actions" },
                { id: 5, name: "Report Findings", aiAction: "Highlights compliance gaps" }
             ]
        },
         {
            id: "research-dev", name: "Research & Dev", fteCostEstimate: 8000,
            icon: <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 12c0-1.232-.046-2.453-.138-3.662m-16.862 0A8.98 8.98 0 0112 3.044m0 18.012c4.418 0 8-3.582 8-8s-3.582-8-8-8-8 3.582-8 8 3.582 8 8 8zm0-14.012a6.006 6.006 0 100 12.012 6.006 6.006 0 000-12.012z" />,
            description: "Accelerate innovation by automating literature reviews, experiment simulation, data analysis, and report generation.",
            workflowHighlights: [
                { id: 1, name: "Scan Literature", aiAction: "Summarizes relevant papers" },
                { id: 2, name: "Simulate Experiments", aiAction: "Models potential outcomes" },
                { id: 3, name: "Analyze Results", aiAction: "Finds patterns in data" },
                { id: 4, name: "Generate Hypotheses", aiAction: "Suggests new research paths" },
                { id: 5, name: "Draft Reports", aiAction: "Formats experimental findings" }
             ]
        },
         {
             id: "content-creation", name: "Content Creation", fteCostEstimate: 4800,
             icon: <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10" />,
             description: "Automate drafting articles, social media posts, product descriptions, and email copy based on prompts and data.",
             workflowHighlights: [
                 { id: 1, name: "Define Brief", aiAction: "Analyzes keywords & goals" },
                 { id: 2, name: "Generate Drafts", aiAction: "Creates initial text variations" },
                 { id: 3, name: "Refine Tone", aiAction: "Adjusts writing style" },
                 { id: 4, name: "Check Uniqueness", aiAction: "Scans against plagiarism" },
                 { id: 5, name: "Format Output", aiAction: "Prepares for CMS/publish" }
              ]
         },
         {
             id: "process-optimization", name: "Process Optimization", fteCostEstimate: 7200,
             icon: <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 13.5l-1.5-1.5m0 0l1.5-1.5m-1.5 1.5h16.5m0 0l1.5 1.5m0 0l-1.5 1.5m-16.5-3l1.5 1.5m0 0l-1.5 1.5m16.5-3l-1.5-1.5m1.5 1.5v4.5A2.25 2.25 0 0118.75 18h-12a2.25 2.25 0 01-2.25-2.25v-4.5m16.5 0v-2.25A2.25 2.25 0 0018.75 6h-12a2.25 2.25 0 00-2.25 2.25V12" />,
             description: "Analyze existing workflows, identify bottlenecks using AI, simulate improvements, and deploy optimized processes.",
             workflowHighlights: [
                 { id: 1, name: "Map Process", aiAction: "Visualizes current steps" },
                 { id: 2, name: "Identify Bottlenecks", aiAction: "Finds time/cost sinks" },
                 { id: 3, name: "Simulate Changes", aiAction: "Tests potential improvements" },
                 { id: 4, name: "Deploy Optimization", aiAction: "Updates workflow rules" },
                 { id: 5, name: "Monitor KPIs", aiAction: "Tracks efficiency gains" }
              ]
         },
    ];


    const handleCategoryClick = (id) => {
        setActiveCategory(id);
    };

    const selectedCategoryData = automationCategories.find(cat => cat.id === activeCategory);

    // SVG Flowchart Constants
    const svgHeight = 150;
    const nodeRadius = 14;
    const nodeOuterRadius = nodeRadius + 4;
    const numNodes = 5;
    const svgPaddingX = 20;
    const svgWidth = 700 + 2 * svgPaddingX;
    const contentWidth = svgWidth - 2 * svgPaddingX;
    // Add right offset to move nodes to the right
    const rightOffset = 50; 
    const nodeSpacing = ((contentWidth - rightOffset) - nodeOuterRadius * 2 * numNodes) / (numNodes - 1);
    const startX = svgPaddingX + nodeOuterRadius + rightOffset;
    const lineY = svgHeight / 2 - 25;
    const textY1 = lineY + nodeRadius + 16;
    const textY2 = textY1 + 14;
    const lineLength = nodeSpacing + nodeRadius * 2;

    return (
        // Added ID="our-products" here for the hero button link
        <section id="our-products" ref={sectionRef} className="bg-black text-gray-300 py-20 md:py-28 px-6 md:px-8 border-b border-gray-800/50 relative overflow-hidden">
            <div className="absolute inset-0 z-0 opacity-[0.03]" style={{ backgroundImage: `radial-gradient(#a855f7 1px, transparent 1px)`, backgroundSize: '30px 30px' }}></div>
            <div className="absolute inset-0 -z-10 bg-black"></div>

            <div className="max-w-7xl mx-auto relative z-10">
                {/* Section Header */}
                <div className={`max-w-3xl mx-auto text-center mb-16 md:mb-20 transition-opacity duration-700 ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
                    <h2 className={`text-sm font-semibold ${ACCENT_COLOR_TEXT} uppercase tracking-widest mb-3`}>
                        Platform Architecture
                    </h2>
                    <h3 className={`text-3xl md:text-4xl font-medium ${HEADING_COLOR} tracking-tight mb-4`}>
                        Integrated AI Automation
                    </h3>
                    <p className={`text-base md:text-lg ${BASE_TEXT_COLOR} max-w-2xl mx-auto leading-relaxed`}>
                        Leverage our core engine and specialized solutions to build sophisticated, automated workflows across your entire value chain.
                    </p>
                </div>

                {/* Core AI Engine Hub - Removed "Core Engine" Text */}
                 <div className={`core-engine-minimal mb-16 md:mb-20 transition-opacity duration-700 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '0.2s' }}>
                     {/* <h4 className={`text-center text-xs ${SUBTLE_TEXT_COLOR} uppercase tracking-wider mb-8`}>Core Engine</h4> */}
                     <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 max-w-4xl mx-auto text-center md:text-left">
                         <div>
                            <h5 className={`text-base font-medium ${ACCENT_COLOR_TEXT} mb-2`}>Foundation Models</h5>
                            <ul className="space-y-1.5 text-sm text-gray-400">
                                <li>Echidna v4o (Reasoning)</li>
                                <li>Sonic v5 (Speed)</li>
                                <li>Athena v3 (Specialized)</li>
                            </ul>
                        </div>
                        <div>
                            <h5 className={`text-base font-medium ${ACCENT_COLOR_TEXT} mb-2`}>Agent Framework</h5>
                            <ul className="space-y-1.5 text-sm text-gray-400">
                                <li>Autonomous Agents</li>
                                <li>Collaborative Swarms</li>
                                <li>Human-in-the-loop</li>
                            </ul>
                        </div>
                        <div>
                            <h5 className={`text-base font-medium ${ACCENT_COLOR_TEXT} mb-2`}>Infrastructure</h5>
                            <ul className="space-y-1.5 text-sm text-gray-400">
                                <li>Enterprise Security</li>
                                <li>Scalable Computing</li>
                                <li>Integration Fabric</li>
                            </ul>
                        </div>
                    </div>
                     <hr className="border-t border-gray-800/50 max-w-xl mx-auto mt-10" />
                 </div>

                {/* Automation Solutions - Two Pane Layout */}
                 <div className={`automation-solutions-minimal grid grid-cols-1 md:grid-cols-4 gap-12 md:gap-16 transition-opacity duration-700 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`} style={{ transitionDelay: '0.4s' }}>
                     {/* Left Pane: Categories List */}
                     <div className="md:col-span-1">
                         <h4 className={`text-xs ${SUBTLE_TEXT_COLOR} uppercase tracking-wider mb-6 px-3 md:px-0`}>Solutions</h4>
                         <ul className="space-y-1">
                             {automationCategories.map((category, index) => (
                                 <li key={category.id} className={`list-item-wrapper transition-opacity duration-300 ${isAnimated ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: `${0.5 + index * GLOBAL_PLATFORM_LIST_ITEM_STAGGER}s`}}>
                                     <div
                                        onClick={() => handleCategoryClick(category.id)}
                                        onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && handleCategoryClick(category.id)}
                                        role="button"
                                        tabIndex={0}
                                        className={`category-list-item p-3 border-l-2 transition-all duration-300 cursor-pointer ${activeCategory === category.id ? `${ACCENT_COLOR_BORDER_ACTIVE} bg-gray-950/50` : `${ACCENT_COLOR_BORDER} border-opacity-30 hover:border-opacity-100 hover:bg-gray-950/30`}`}
                                     >
                                         <h5 className={`text-sm font-medium ${activeCategory === category.id ? HEADING_COLOR : BASE_TEXT_COLOR} transition-colors`}>{category.name}</h5>
                                     </div>
                                 </li>
                             ))}
                         </ul>
                     </div>

                     {/* Right Pane: Details & Flowchart */}
                     <div className="md:col-span-3 min-h-[450px] md:min-h-full relative border border-gray-800/50 rounded-sm p-6 md:p-8 overflow-hidden">
                         {/* Placeholder */}
                          <div className={`placeholder-panel absolute inset-0 flex items-center justify-center text-center transition-opacity duration-300 ease-out ${activeCategory ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                              <p className={`text-sm ${SUBTLE_TEXT_COLOR} max-w-xs`}>
                                Select an automation solution to view key capabilities, workflow stages, and potential cost benefits.
                              </p>
                          </div>

                         {/* Details Panel */}
                          {automationCategories.map((category) => (
                             <div
                                key={category.id}
                                className={`details-panel absolute inset-0 p-6 md:p-8 transition-opacity duration-500 ease-out ${activeCategory === category.id ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                             >
                                 {activeCategory === category.id && selectedCategoryData && (
                                     <>
                                         <div className="flex justify-between items-start mb-4">
                                             <div>
                                                  <svg xmlns="http://www.w3.org/2000/svg" className={`h-6 w-6 mb-2 ${ACCENT_COLOR_TEXT}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                                                     {selectedCategoryData.icon}
                                                 </svg>
                                                 <h4 className={`text-lg font-medium ${HEADING_COLOR}`}>{selectedCategoryData.name} Automation</h4>
                                             </div>
                                             <div
                                                onClick={() => setActiveCategory(null)}
                                                onKeyPress={(e) => (e.key === 'Enter' || e.key === ' ') && setActiveCategory(null)}
                                                role="button" tabIndex={0} title="Close Details"
                                                className={`p-1 text-gray-600 hover:text-gray-300 transition cursor-pointer -mr-2 -mt-2`}
                                             >
                                                 <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                                                </svg>
                                            </div>
                                         </div>
                                         <p className="text-sm text-gray-400 mb-8 leading-relaxed">{selectedCategoryData.description}</p>

                                         <h5 className={`text-xs ${SUBTLE_TEXT_COLOR} uppercase tracking-wider mb-6`}>Workflow Stages & AI Actions</h5>

                                         {/* --- Animated SVG Flowchart --- */}
                                         <div className="flex items-center justify-start mb-8 overflow-x-auto pb-4"> {/* Ensure scroll */}
                                              <svg width={svgWidth} height={svgHeight} viewBox={`0 0 ${svgWidth} ${svgHeight}`} className={`flowchart-svg active flex-shrink-0`}>
                                                  {/* Lines */}
                                                  {selectedCategoryData.workflowHighlights.slice(0, -1).map((_, idx) => {
                                                      const x1 = startX + idx * (nodeRadius * 2 + nodeSpacing) + nodeRadius;
                                                      const x2 = x1 + lineLength;
                                                      const currentLineLength = x2 - x1;
                                                      const animationDelay = GLOBAL_PLATFORM_FLOW_LINE_DELAY + (idx * GLOBAL_PLATFORM_FLOW_NODE_STAGGER) + GLOBAL_PLATFORM_FLOW_NODE_APPEAR * 0.5;

                                                      return (
                                                          <line
                                                              key={`line-${category.id}-${idx}`}
                                                              x1={x1} y1={lineY} x2={x2} y2={lineY}
                                                              stroke={SVG_LINE_COLOR} strokeWidth="1" strokeOpacity="0.5"
                                                              className="flowchart-line"
                                                              strokeDasharray={currentLineLength} strokeDashoffset={currentLineLength}
                                                              style={{ animationDelay: `${animationDelay}s`, '--line-length': currentLineLength }}
                                                          />
                                                      );
                                                  })}
                                                  {/* Nodes */}
                                                  {selectedCategoryData.workflowHighlights.map((step, idx) => {
                                                      const cx = startX + idx * (nodeRadius * 2 + nodeSpacing);
                                                      const nodeAnimationDelay = idx * GLOBAL_PLATFORM_FLOW_NODE_STAGGER;
                                                      return (
                                                          <g key={step.id} className="flowchart-step" style={{ animationDelay: `${nodeAnimationDelay}s` }}>
                                                              <circle cx={cx} cy={lineY} r={nodeRadius} fill="#1f2937" stroke={ACCENT_COLOR_TEXT.replace('text-','stroke-')} strokeWidth="1.5" className="flowchart-node-circle" />
                                                              <text x={cx} y={lineY} dy="0.35em" textAnchor="middle" fontSize="11" fontWeight="600" fill="#f3f4f6" className="flowchart-node-number select-none pointer-events-none">{step.id}</text>
                                                              <text x={cx} y={textY1} textAnchor="middle" fontSize="10" fontWeight="500" fill="#d1d5db" className="flowchart-node-text">{step.name}</text>
                                                              <text x={cx} y={textY2} textAnchor="middle" fontSize="9" fill="#9ca3af" className="flowchart-node-text">{step.aiAction}</text>
                                                          </g>
                                                      );
                                                  })}
                                              </svg>
                                         </div>
                                         {/* --- End of SVG Flowchart --- */}

                                         {/* --- Cost Comparison Slider --- */}
                                         <CostComparisonSlider
                                            fteCost={selectedCategoryData.fteCostEstimate}
                                            accentColor={ACCENT_COLOR_TEXT}
                                            isVisible={activeCategory === category.id}
                                         />
                                     </>
                                 )}
                             </div>
                         ))}
                     </div>
                 </div>
            </div>
        </section>
    );
}


// --- Ecosystem Section - Minimalist Style ---
function EcosystemSection() {
    const [isAnimated, setIsAnimated] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsAnimated(true); observer.unobserve(entry.target); } },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.disconnect(); };
    }, []);

    const STRATEGY_SESSION_LINK = "https://calendly.com/manas-o4af/30min";

    return (
        <section ref={sectionRef} className="bg-black text-gray-300 py-20 md:py-28 px-6 md:px-8 border-b border-gray-800/50 relative overflow-hidden">
             <div className="absolute inset-0 z-0 opacity-[0.02]" style={{ backgroundImage: `radial-gradient(#facc15 0.5px, transparent 0.5px), radial-gradient(#a855f7 0.5px, transparent 0.5px)`, backgroundSize: `40px 40px, 40px 40px`, backgroundPosition: `0 0, 20px 20px` }}></div>
             <div className="absolute inset-0 -z-10 bg-black"></div>

            <div className="max-w-6xl mx-auto flex flex-col md:flex-row-reverse items-center gap-12 md:gap-16 relative z-10">
                 {/* SVG Visualizer */}
                 <div className={`w-full md:w-1/2 flex justify-center items-center aspect-square md:aspect-auto min-h-[300px] md:min-h-[420px] transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '0.3s'}}>
                    <svg viewBox="0 0 400 400" className="w-full h-full max-w-xs sm:max-w-sm md:max-w-md" preserveAspectRatio="xMidYMid meet">
                        <defs> <radialGradient id="ecoHubGrad"> <stop offset="0%" stopColor="#fef9c3"/> <stop offset="100%" stopColor="#facc15" stopOpacity="0.7"/> </radialGradient> <filter id="ecoGlow" x="-70%" y="-70%" width="240%" height="240%"> <feGaussianBlur stdDeviation="5" result="blur"/> <feFlood floodColor="#fde047" floodOpacity="0.4" result="color"/> <feComposite in="color" in2="blur" operator="in" result="glow"/> <feMerge> <feMergeNode in="glow"/> <feMergeNode in="SourceGraphic"/> </feMerge> </filter> <symbol id="icon-foundry" viewBox="0 0 24 24"> <path strokeLinecap="round" strokeLinejoin="round" d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" /> </symbol> <symbol id="icon-anne" viewBox="0 0 24 24"> <path strokeLinecap="round" strokeLinejoin="round" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /> </symbol> <symbol id="icon-agents" viewBox="0 0 20 20"> <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" /> </symbol> <symbol id="icon-llm" viewBox="0 0 24 24"> <path strokeLinecap="round" strokeLinejoin="round" d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /> </symbol> </defs>
                        <g className={`ecosystem-hub ${isAnimated ? 'animate-ecosystem-hub-enter' : ''}`}> <circle cx="200" cy="200" r="40" fill="url(#ecoHubGrad)" filter="url(#ecoGlow)" className="ecosystem-hub-pulse"/> <text x="200" y="205" textAnchor="middle" fontSize="10" fill="#a16207" fontWeight="bold">SYNERGY</text> </g>
                        <g className={`ecosystem-elements ${isAnimated ? 'animate-ecosystem-elements-enter' : ''}`}> <line x1="200" y1="160" x2="200" y2="90" stroke="#facc15" strokeWidth="1" className="ecosystem-line" style={{ animationDelay: '0.4s' }}/> <use xlinkHref="#icon-foundry" x="188" y="60" width="24" height="24" fill="#c084fc" className="ecosystem-icon" style={{ '--icon-delay': '0.6s' }}/> <line x1="240" y1="200" x2="310" y2="200" stroke="#facc15" strokeWidth="1" className="ecosystem-line" style={{ animationDelay: '0.5s' }}/> <use xlinkHref="#icon-anne" x="320" y="188" width="24" height="24" fill="#22d3ee" className="ecosystem-icon" style={{ '--icon-delay': '0.7s' }}/> <line x1="200" y1="240" x2="200" y2="310" stroke="#facc15" strokeWidth="1" className="ecosystem-line" style={{ animationDelay: '0.6s' }}/> <use xlinkHref="#icon-agents" x="190" y="320" width="20" height="20" fill="#34d399" className="ecosystem-icon" style={{ '--icon-delay': '0.8s' }}/> <line x1="160" y1="200" x2="90" y2="200" stroke="#facc15" strokeWidth="1" className="ecosystem-line" style={{ animationDelay: '0.7s' }}/> <use xlinkHref="#icon-llm" x="60" y="188" width="24" height="24" fill="#a855f7" className="ecosystem-icon" style={{ '--icon-delay': '0.9s' }}/> </g>
                    </svg>
                 </div>
                 {/* Text Content */}
                 <div className={`w-full md:w-1/2 text-center md:text-left transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
                     <h2 className="text-3xl md:text-4xl font-medium text-gray-100 mb-4 tracking-tight"> Unlock Synergistic Value with <span className="text-yellow-400">Integrated AI</span> </h2>
                    <p className="text-base md:text-lg text-gray-400 mb-8 max-w-lg mx-auto md:mx-0 leading-relaxed">
                      Combine the power of Echidna Foundry, Anne SDR, Core Agents, and Foundational Models. Create end-to-end automated solutions that deliver exponential value.
                    </p>
                    <div className="space-y-5 mb-10 text-left max-w-md mx-auto md:mx-0">
                        {/* Feature Items */}
                        <div className="flex items-start gap-3 group">
                            <div className="w-px h-4 bg-yellow-600/50 mt-1 group-hover:bg-yellow-400 transition-colors"></div>
                            <div>
                                <h4 className="font-medium text-gray-200 text-sm mb-0.5">Cross-Functional Automation</h4>
                                <p className="text-xs text-gray-500 leading-snug">Bridge silos between sales, marketing, operations, and support seamlessly.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 group">
                            <div className="w-px h-4 bg-yellow-600/50 mt-1 group-hover:bg-yellow-400 transition-colors"></div>
                            <div>
                                <h4 className="font-medium text-gray-200 text-sm mb-0.5">Unified Data Intelligence</h4>
                                <p className="text-xs text-gray-500 leading-snug">Gain holistic insights by connecting data across all automated processes.</p>
                            </div>
                        </div>
                        <div className="flex items-start gap-3 group">
                            <div className="w-px h-4 bg-yellow-600/50 mt-1 group-hover:bg-yellow-400 transition-colors"></div>
                            <div>
                                <h4 className="font-medium text-gray-200 text-sm mb-0.5">Compounding Efficiency</h4>
                                <p className="text-xs text-gray-500 leading-snug">Maximize ROI as integrated automations amplify each other's impact over time.</p>
                            </div>
                        </div>
                    </div>
                    {/* Clickable Div */}
                    <div
                        onClick={() => handleNavigate(STRATEGY_SESSION_LINK)}
                        className="border border-yellow-600 hover:border-yellow-400 text-yellow-300 hover:text-white font-medium py-2.5 px-6 rounded-md transition duration-300 ease-in-out text-base inline-block cursor-pointer group"
                        role="button"
                        tabIndex={0}
                        onKeyPress={(e) => e.key === 'Enter' && handleNavigate(STRATEGY_SESSION_LINK)}
                    >
                        Book Strategy Session <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-1">→</span>
                    </div>
                </div>
            </div>
        </section>
    );
}

// --- Security Section - Minimalist Style ---
function SecurityInfraSection() {
    const [isAnimated, setIsAnimated] = useState(false);
    const sectionRef = useRef(null);
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsAnimated(true); observer.unobserve(entry.target); } },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.disconnect(); };
    }, []);

     const weavePaths = [ "M 50 150 Q 150 50, 200 200 T 350 250", "M 350 150 Q 250 50, 200 200 T 50 250", "M 150 50 Q 50 150, 200 200 T 250 350", "M 250 50 Q 350 150, 200 200 T 150 350", "M 50 250 C 100 350, 300 350, 350 250", "M 350 150 C 300 50, 100 50, 50 150", ];
     const nodePositions = [ { cx: 200, cy: 200 }, { cx: 150, cy: 125 }, { cx: 250, cy: 125 }, { cx: 150, cy: 275 }, { cx: 250, cy: 275 }, { cx: 100, cy: 200 }, { cx: 300, cy: 200 } ];
    return (
        <section ref={sectionRef} className="bg-black text-gray-300 py-20 md:py-28 px-6 md:px-8 border-b border-gray-800/50 relative overflow-hidden">
             <div className="absolute inset-0 z-0 opacity-[0.03]" style={{ backgroundImage: `radial-gradient(#14b8a6 0.5px, transparent 0.5px)`, backgroundSize: '35px 35px' }}></div>
             <div className="absolute inset-0 -z-10 bg-black"></div>
             <div className="absolute inset-0 z-0 opacity-[0.08]" style={{ backgroundImage: 'radial-gradient(at 10% 90%, hsla(175, 70%, 40%, 0.05) 0px, transparent 60%), radial-gradient(at 90% 10%, hsla(190, 60%, 50%, 0.05) 0px, transparent 55%)', filter: 'blur(10px)', }}></div>


            <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-12 md:gap-16 relative z-10">
                {/* SVG Visualizer */}
                 <div className={`w-full md:w-1/2 flex justify-center items-center aspect-square md:aspect-auto min-h-[300px] md:min-h-[420px] transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`} style={{transitionDelay: '0.3s'}}>
                    <svg viewBox="0 0 400 400" className="w-full h-full max-w-xs sm:max-w-sm md:max-w-md" preserveAspectRatio="xMidYMid meet">
                        <defs> <linearGradient id="securityWeaveGrad" gradientUnits="userSpaceOnUse" x1="0" y1="200" x2="400" y2="200"> <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.8"/> <stop offset="50%" stopColor="#99f6e4" stopOpacity="0.9"/> <stop offset="100%" stopColor="#0d9488" stopOpacity="0.8"/> </linearGradient> <radialGradient id="securitySparkGrad"> <stop offset="0%" stopColor="#ffffff" stopOpacity="1"/> <stop offset="80%" stopColor="#ccfbf1" stopOpacity="0.5"/> <stop offset="100%" stopColor="#99f6e4" stopOpacity="0"/> </radialGradient> <filter id="securityWeaveGlow" x="-50%" y="-50%" width="200%" height="200%"> <feGaussianBlur stdDeviation="3" result="blur" /> <feFlood floodColor="#5eead4" floodOpacity="0.2" result="glowColor"/> <feComposite in="glowColor" in2="blur" operator="in" result="softGlow_colored"/> <feMerge> <feMergeNode in="softGlow_colored"/> <feMergeNode in="SourceGraphic"/> </feMerge> </filter> <clipPath id="securityBounds"> <rect x="0" y="0" width="400" height="400" /> </clipPath> </defs>
                        <g className={`security-weave-visualizer ${isAnimated ? 'animate-security-weave-enter' : ''}`} filter="url(#securityWeaveGlow)">
                            <g className="security-weave-paths"> {weavePaths.map((pathData, index) => ( <path key={`weave-${index}`} id={`weavePath${index}`} d={pathData} fill="none" stroke="url(#securityWeaveGrad)" strokeWidth={index % 2 === 0 ? "1" : "0.7"} strokeLinecap="round" className="security-weave-line" style={{ '--flow-offset-mult': `${1.5 + index * 0.2}`, '--flow-delay': `${index * 0.15}s`, opacity: 0.7 - index*0.05 }} /> ))} </g>
                             <g className="security-nodes"> {nodePositions.map((node, index) => ( <circle key={`node-${index}`} cx={node.cx} cy={node.cy} r="2.5" fill="#99f6e4" stroke="#ffffff" strokeWidth="0.5" className="security-node" style={{ animationDelay: `${0.5 + index * 0.2}s` }} /> ))} </g>
                            <g className="security-sparks" clipPath="url(#securityBounds)"> <circle r="3" fill="url(#securitySparkGrad)" className="security-spark"> <animateMotion dur={`${GLOBAL_SECURITY_SPARK_TRAVEL_DURATION}s`} repeatCount="indefinite" rotate="auto" begin="0s"> <mpath xlinkHref="#weavePath0"/> </animateMotion> </circle> <circle r="2.5" fill="url(#securitySparkGrad)" className="security-spark"> <animateMotion dur={`${GLOBAL_SECURITY_SPARK_TRAVEL_DURATION * 1.2}s`} repeatCount="indefinite" rotate="auto" begin="1.5s"> <mpath xlinkHref="#weavePath3"/> </animateMotion> </circle> <circle r="3" fill="url(#securitySparkGrad)" className="security-spark"> <animateMotion dur={`${GLOBAL_SECURITY_SPARK_TRAVEL_DURATION * 0.9}s`} repeatCount="indefinite" rotate="auto" begin="3s"> <mpath xlinkHref="#weavePath5"/> </animateMotion> </circle> </g>
                        </g>
                    </svg>
                </div>
                {/* Text Content */}
                 <div className={`w-full md:w-1/2 text-center md:text-left transition-opacity duration-1000 ease-out ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
                    <h2 className="text-3xl md:text-4xl font-medium text-gray-100 mb-4 tracking-tight"> Enterprise-Grade <span className="text-teal-400">Security & Trust</span> </h2>
                    <p className="text-base md:text-lg text-gray-400 mb-8 max-w-lg mx-auto md:mx-0 leading-relaxed">
                      Protecting your strategic assets is paramount. Our platform integrates robust security protocols, continuous monitoring, and adherence to stringent global compliance standards.
                    </p>
                    <div className="space-y-5 mb-10 text-left max-w-md mx-auto md:mx-0">
                        {/* Feature Items */}
                         <div className="flex items-start gap-3 group">
                            <div className="w-px h-4 bg-teal-600/50 mt-1 group-hover:bg-teal-400 transition-colors"></div>
                            <div>
                                <h4 className="font-medium text-gray-200 text-sm mb-0.5">Compliance Assured (GDPR, SOC 2*)</h4>
                                <p className="text-xs text-gray-500 leading-snug">Meeting rigorous international standards for data privacy and security controls.</p>
                            </div>
                        </div>
                         <div className="flex items-start gap-3 group">
                            <div className="w-px h-4 bg-teal-600/50 mt-1 group-hover:bg-teal-400 transition-colors"></div>
                            <div>
                                <h4 className="font-medium text-gray-200 text-sm mb-0.5">Multi-Layered Data Encryption</h4>
                                <p className="text-xs text-gray-500 leading-snug">Securing data end-to-end, both at rest and in transit, using advanced ciphers.</p>
                            </div>
                        </div>
                         <div className="flex items-start gap-3 group">
                            <div className="w-px h-4 bg-teal-600/50 mt-1 group-hover:bg-teal-400 transition-colors"></div>
                            <div>
                                <h4 className="font-medium text-gray-200 text-sm mb-0.5">Proactive Threat Monitoring</h4>
                                <p className="text-xs text-gray-500 leading-snug">Continuous vigilance against emerging threats and system vulnerabilities 24/7.</p>
                            </div>
                        </div>
                    </div>
                    {/* Compliance Badges */}
                     <div className="flex items-center justify-center md:justify-start gap-4 mt-6">
                        <span className="py-1 px-3 border border-teal-700/50 rounded-md text-xs font-medium text-teal-300/80">GDPR</span>
                        <span className="py-1 px-3 border border-teal-700/50 rounded-md text-xs font-medium text-teal-300/80">SOC 2 *</span>
                    </div>
                    <p className="text-xs text-teal-500/70 mt-2 text-center md:text-left">* Certification In Progress</p>
                </div>
            </div>
        </section>
    );
}

// --- Call To Action Section - Minimalist Style ---
function CallToActionSection() {
    const CONSULTATION_LINK = "https://calendly.com/manas-o4af/30min";

    return (
      <section id="contact" className="bg-black text-gray-300 py-20 md:py-28 px-6 text-center border-t border-gray-800/50">
        <div className="max-w-xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-medium text-gray-100 mb-4 tracking-tight"> Ready to Automate Intelligently? </h2>
          <p className="text-base md:text-lg text-gray-400 mb-10 leading-relaxed">
            Discuss how Metabees AI can tailor an automation strategy to unlock your organization's full potential.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            {/* Minimalist Purple Button/Div */}
            <div
                onClick={() => handleNavigate(CONSULTATION_LINK)}
                className="bg-purple-600 hover:bg-purple-700 text-white font-medium py-3 px-8 rounded-md transition duration-300 ease-in-out text-base shadow-md hover:shadow-purple-500/30 cursor-pointer"
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleNavigate(CONSULTATION_LINK)}
            >
                Book a Consultation
            </div>
            {/* Minimalist Outline Button/Div */}
           <a href="/login"> <div
                onClick={() => handleNavigate('#tech-platform')}
                className="border border-purple-600 hover:border-purple-400 text-purple-300 hover:text-white font-medium py-[10px] px-8 rounded-md transition duration-300 ease-in-out text-base cursor-pointer group"
                role="button"
                tabIndex={0}
                onKeyPress={(e) => e.key === 'Enter' && handleNavigate('#tech-platform')}
            >
                 Explore Platform <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-300 ml-1">→</span>
            </div></a>
          </div>
        </div>
      </section>
    );
}

// --- Footer Section - Minimalist Style ---
function FooterSection() {
    const CONSULTATION_LINK = "https://calendly.com/manas-o4af/30min";
    const [isAnimated, setIsAnimated] = useState(false);
    const sectionRef = useRef(null);
    const globeRef = useRef(null);
    const [activeLocation, setActiveLocation] = useState(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) { setIsAnimated(true); observer.unobserve(entry.target); } },
            { threshold: 0.2 }
        );
        if (sectionRef.current) observer.observe(sectionRef.current);
        return () => { if (sectionRef.current) observer.disconnect(); };
    }, []);
    
    useEffect(() => {
        if (!isAnimated || !globeRef.current) return;
        
        // Create globe visualization
        const container = globeRef.current;
        let scene, camera, renderer, globe, dots = [];
        let animationFrameId;
        let isDragging = false;
        let previousMousePosition = { x: 0, y: 0 };
        let rotationSpeed = { x: 0.001, y: 0.001 };
        let autoRotate = true;
        
        // Office locations
        const locations = [
            { id: "sf", name: "San Francisco", lat: 37.7749, lng: -122.4194, team: "Design Team" },
            { id: "bangalore", name: "Bangalore", lat: 12.9716, lng: 77.5946, team: "R&D Team" },
            { id: "delhi", name: "New Delhi", lat: 28.6139, lng: 77.2090, team: "Sales Team" }
        ];
        
        const init = () => {
            // Setup scene
            scene = new THREE.Scene();
            camera = new THREE.PerspectiveCamera(75, container.clientWidth / container.clientHeight, 0.1, 1000);
            camera.position.z = 200;
            
            renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
            renderer.setSize(container.clientWidth, container.clientHeight);
            renderer.setPixelRatio(window.devicePixelRatio);
            container.appendChild(renderer.domElement);
            
            // Create globe
            const globeGeometry = new THREE.SphereGeometry(60, 64, 64);
            const globeMaterial = new THREE.MeshBasicMaterial({
                color: 0x1a1a1a,
                wireframe: true,
                transparent: true,
                opacity: 0.3
            });
            globe = new THREE.Mesh(globeGeometry, globeMaterial);
            scene.add(globe);
            
            // Add glow effect
            const glowGeometry = new THREE.SphereGeometry(62, 64, 64);
            const glowMaterial = new THREE.ShaderMaterial({
                uniforms: {
                    color1: { value: new THREE.Color(0x9333ea) }, 
                    color2: { value: new THREE.Color(0x000000) },
                    viewVector: { value: camera.position }
                },
                vertexShader: `
                    uniform vec3 viewVector;
                    varying float intensity;
                    void main() {
                        vec3 vNormal = normalize(normalMatrix * normal);
                        vec3 vNormel = normalize(normalMatrix * viewVector);
                        intensity = pow(0.5 - dot(vNormal, vNormel), 2.0);
                        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
                    }
                `,
                fragmentShader: `
                    uniform vec3 color1;
                    uniform vec3 color2;
                    varying float intensity;
                    void main() {
                        vec3 glow = mix(color1, color2, 1.0 - intensity);
                        gl_FragColor = vec4(glow, 0.15 * intensity);
                    }
                `,
                side: THREE.BackSide,
                blending: THREE.AdditiveBlending,
                transparent: true
            });
            
            const glowMesh = new THREE.Mesh(glowGeometry, glowMaterial);
            scene.add(glowMesh);
            
            // Add location dots
            locations.forEach(location => {
                // Convert latitude and longitude to 3D position
                const phi = (90 - location.lat) * (Math.PI / 180);
                const theta = (location.lng + 180) * (Math.PI / 180);
                
                const x = -((62.5) * Math.sin(phi) * Math.cos(theta));
                const y = ((62.5) * Math.cos(phi));
                const z = ((62.5) * Math.sin(phi) * Math.sin(theta));
                
                // Create dot
                const dotGeometry = new THREE.SphereGeometry(1.5, 16, 16);
                const dotMaterial = new THREE.MeshBasicMaterial({ 
                    color: 0xc084fc,
                    transparent: true,
                    opacity: 0.7
                });
                const dot = new THREE.Mesh(dotGeometry, dotMaterial);
                
                dot.position.set(x, y, z);
                dot.userData = { 
                    isLocation: true, 
                    locationId: location.id,
                    locationName: location.name
                };
                scene.add(dot);
                dots.push({ mesh: dot, location });
                
                // Add pulse effect for each dot
                const pulseGeometry = new THREE.SphereGeometry(1, 16, 16);
                const pulseMaterial = new THREE.MeshBasicMaterial({ 
                    color: 0xc084fc,
                    transparent: true,
                    opacity: 0.4
                });
                const pulse = new THREE.Mesh(pulseGeometry, pulseMaterial);
                pulse.position.set(x, y, z);
                pulse.scale.set(1, 1, 1);
                pulse.userData = { pulse: true, baseSize: 1, factor: Math.random() };
                scene.add(pulse);
                dots.push({ mesh: pulse, isPulse: true });
            });
            
            // Add click event to container
            container.addEventListener('mousedown', onMouseDown);
            container.addEventListener('mousemove', onMouseMove);
            container.addEventListener('mouseup', onMouseUp);
            container.addEventListener('mouseleave', onMouseUp);
            container.addEventListener('click', onContainerClick);
            
            animate();
        };
        
        const onMouseDown = (event) => {
            isDragging = true;
            previousMousePosition = {
                x: event.clientX,
                y: event.clientY
            };
            autoRotate = false;
        };
        
        const onMouseMove = (event) => {
            if (!isDragging) return;
            
            const deltaMove = {
                x: event.clientX - previousMousePosition.x,
                y: event.clientY - previousMousePosition.y
            };
            
            // Apply rotation
            globe.rotation.y += deltaMove.x * 0.005;
            globe.rotation.x += deltaMove.y * 0.005;
            
            previousMousePosition = {
                x: event.clientX,
                y: event.clientY
            };
        };
        
        const onMouseUp = () => {
            isDragging = false;
            // Resume auto-rotation after a delay
            setTimeout(() => {
                autoRotate = true;
            }, 3000);
        };
        
        const onContainerClick = (event) => {
            if (isDragging) return; // Don't register click if dragging
            
            // Get mouse position
            const rect = container.getBoundingClientRect();
            const mouse = new THREE.Vector2(
                ((event.clientX - rect.left) / container.clientWidth) * 2 - 1,
                -((event.clientY - rect.top) / container.clientHeight) * 2 + 1
            );
            
            // Raycasting to detect clicks on dots
            const raycaster = new THREE.Raycaster();
            raycaster.setFromCamera(mouse, camera);
            
            // Find intersections with dots
            const dotMeshes = dots
                .filter(dot => dot.mesh.userData.isLocation)
                .map(dot => dot.mesh);
            
            const intersects = raycaster.intersectObjects(dotMeshes);
            
            if (intersects.length > 0) {
                const clickedDot = intersects[0].object;
                const locationId = clickedDot.userData.locationId;
                
                // Highlight the dot and update UI
                setActiveLocation(locationId);
                
                // Animate the dot
                clickedDot.scale.set(1.5, 1.5, 1.5);
                setTimeout(() => {
                    clickedDot.scale.set(1, 1, 1);
                }, 300);
            }
        };
        
        const animate = () => {
            if (autoRotate) {
                globe.rotation.y += rotationSpeed.y;
                globe.rotation.x += rotationSpeed.x;
            }
            
            // Update pulse effects
            dots.forEach(dot => {
                if (dot.isPulse) {
                    const pulse = dot.mesh;
                    pulse.userData.factor += 0.025;
                    const scale = 1 + Math.sin(pulse.userData.factor) * 0.5;
                    pulse.scale.set(scale, scale, scale);
                    pulse.material.opacity = 0.4 * (1.5 - scale) + 0.2;
                }
            });
            
            renderer.render(scene, camera);
            animationFrameId = requestAnimationFrame(animate);
        };
        
        // Handle window resize
        const handleResize = () => {
            if (camera && renderer && container) {
                camera.aspect = container.clientWidth / container.clientHeight;
                camera.updateProjectionMatrix();
                renderer.setSize(container.clientWidth, container.clientHeight);
            }
        };
        
        window.addEventListener('resize', handleResize);
        
        init();
        
        return () => {
            window.removeEventListener('resize', handleResize);
            container.removeEventListener('mousedown', onMouseDown);
            container.removeEventListener('mousemove', onMouseMove);
            container.removeEventListener('mouseup', onMouseUp);
            container.removeEventListener('mouseleave', onMouseUp);
            container.removeEventListener('click', onContainerClick);
            
            cancelAnimationFrame(animationFrameId);
            if (renderer && renderer.domElement && container.contains(renderer.domElement)) {
                container.removeChild(renderer.domElement);
            }
            
            // Clean up
            dots.forEach(dot => {
                scene.remove(dot.mesh);
                if (dot.mesh.geometry) dot.mesh.geometry.dispose();
                if (dot.mesh.material) dot.mesh.material.dispose();
            });
            
            if (globe) {
                scene.remove(globe);
                if (globe.geometry) globe.geometry.dispose();
                if (globe.material) globe.material.dispose();
            }
        };
    }, [isAnimated, activeLocation]);

    return (
        <footer ref={sectionRef} className="bg-black text-gray-600 py-12 px-6 border-t border-gray-800/50">
            <div className="max-w-6xl mx-auto">
                {/* Section Header */}
                <div className={`text-center mb-8 transition-opacity duration-700 ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
                    <h2 className="text-2xl font-medium text-gray-100 mb-2">Our Global Presence</h2>
                    <p className="text-sm text-gray-400">Strategic hubs for innovation and growth</p>
                </div>

                {/* Globe Visualization */}
                <div className={`mb-12 transition-opacity duration-700 ${isAnimated ? 'opacity-100' : 'opacity-0'}`}>
                    <div ref={globeRef} className="aspect-[16/9] max-h-[400px] w-full rounded-lg overflow-hidden bg-black/50 border border-gray-800/50 cursor-move"></div>
                    
                    {/* Location List */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
                        {/* San Francisco */}
                        <div 
                            className={`flex items-center space-x-3 group cursor-pointer transition-all duration-300 ${activeLocation === 'sf' ? 'bg-purple-900/20 rounded-md p-2' : 'p-2'}`}
                            onClick={() => setActiveLocation('sf')}
                        >
                            <div className={`w-3 h-3 rounded-full bg-purple-500 transition-all duration-300 ${activeLocation === 'sf' ? 'bg-purple-300 animate-ping' : 'group-hover:bg-purple-300 group-hover:animate-ping'}`}></div>
                            <div>
                                <h3 className="text-gray-300 font-medium">San Francisco</h3>
                                <p className="text-gray-500 text-xs">Design Team</p>
                            </div>
                        </div>

                        {/* Bangalore */}
                        <div 
                            className={`flex items-center space-x-3 group cursor-pointer transition-all duration-300 ${activeLocation === 'bangalore' ? 'bg-purple-900/20 rounded-md p-2' : 'p-2'}`}
                            onClick={() => setActiveLocation('bangalore')}
                        >
                            <div className={`w-3 h-3 rounded-full bg-purple-500 transition-all duration-300 ${activeLocation === 'bangalore' ? 'bg-purple-300 animate-ping' : 'group-hover:bg-purple-300 group-hover:animate-ping'}`}></div>
                            <div>
                                <h3 className="text-gray-300 font-medium">Bangalore</h3>
                                <p className="text-gray-500 text-xs">R&D Team</p>
                            </div>
                        </div>

                        {/* New Delhi */}
                        <div 
                            className={`flex items-center space-x-3 group cursor-pointer transition-all duration-300 ${activeLocation === 'delhi' ? 'bg-purple-900/20 rounded-md p-2' : 'p-2'}`}
                            onClick={() => setActiveLocation('delhi')}
                        >
                            <div className={`w-3 h-3 rounded-full bg-purple-500 transition-all duration-300 ${activeLocation === 'delhi' ? 'bg-purple-300 animate-ping' : 'group-hover:bg-purple-300 group-hover:animate-ping'}`}></div>
                            <div>
                                <h3 className="text-gray-300 font-medium">New Delhi</h3>
                                <p className="text-gray-500 text-xs">Sales Team</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Footer Links */}
                <div className="text-center">
                    <p className="mb-3">© {new Date().getFullYear()} Metabees AI Labs Inc. All rights reserved.</p>
                    <div className="space-x-4">
                        <a href="#" className="hover:text-purple-400 transition-colors duration-200">Privacy Policy</a>
                        <span className="select-none">|</span>
                        <a href="#" className="hover:text-purple-400 transition-colors duration-200">Terms of Service</a>
                        <span className="select-none">|</span>
                        <span
                           onClick={() => handleNavigate(CONSULTATION_LINK)}
                           className="hover:text-purple-400 transition-colors duration-200 cursor-pointer"
                           role="link"
                           tabIndex={0}
                           onKeyPress={(e) => e.key === 'Enter' && handleNavigate(CONSULTATION_LINK)}
                        >
                            Contact Us
                        </span>
                    </div>
                </div>
            </div>
        </footer>
    );
}

// --- Main Page Component ---

export default function Echidna() {
  const pageStyle = {
     fontFamily: "'Inter', system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif"
  };

  const [showUpdatePopup, setShowUpdatePopup] = useState(true);
  const [pageLoaded, setPageLoaded] = useState(false);

  useEffect(() => {
    const styleId = 'dynamic-animations-style-v25-flowchart-details';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.innerHTML = `
        /* Base Styling */
        html, body { background-color: black; color: #d1d5db; overscroll-behavior-y: none; }
        html { scroll-behavior: smooth; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale; }
        body { font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif; font-weight: 400; }
        ::selection { background-color: rgba(168, 85, 247, 0.3); color: white; }

        /* General & Shared Animations */
        @keyframes dots-pulse { 0%, 100% { opacity: 0.07; } 50% { opacity: 0.12; } }
        .pattern-dots { --dot-bg: black; --dot-color: rgba(45, 212, 191, 0.4); --dot-size: 1px; --dot-space: 25px; position: absolute; inset: 0; z-index: 0; background: linear-gradient(90deg, var(--dot-bg) calc(var(--dot-space) - var(--dot-size)), transparent 1%) center, linear-gradient(var(--dot-bg) calc(var(--dot-space) - var(--dot-size)), transparent 1%) center, var(--dot-color); background-size: var(--dot-space) var(--dot-space); animation: dots-pulse 6s ease-in-out infinite; }
        .pattern-grid-flow { position: absolute; inset: 0; z-index: -1; --grid-color: rgba(192, 132, 252, 0.08); background-color: transparent; background-image: linear-gradient(var(--grid-color) 1px, transparent 1px), linear-gradient(90deg, var(--grid-color) 1px, transparent 1px); background-size: 30px 30px; mask-image: linear-gradient(to bottom, transparent, black 30%, black 70%, transparent); animation: grid-flow-anim 15s linear infinite; }
        @keyframes grid-flow-anim { 0% { background-position: 0 0; } 100% { background-position: 0 -300px; } }

        /* Gamification Effects */
        .achievement-counter {
          position: relative;
          transition: all 0.3s ease;
        }
        .achievement-counter:hover {
          transform: scale(1.05);
          text-shadow: 0 0 15px rgba(168, 85, 247, 0.5);
        }
        [role="button"]:active {
          transform: scale(0.98);
        }
        .category-list-item {
          position: relative;
          overflow: hidden;
        }
        .category-list-item:hover::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: linear-gradient(45deg, transparent 0%, rgba(168, 85, 247, 0.1) 50%, transparent 100%);
          animation: shimmer 1.5s infinite;
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .flowchart-node-circle:hover {
          stroke-width: 2;
          filter: brightness(1.2);
          cursor: pointer;
        }
        .tech-badge {
          transition: all 0.3s ease;
        }
        .tech-badge:hover {
          transform: translateY(-3px);
          filter: brightness(1.1);
        }
        .progress-bar-wrapper {
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 3px;
          z-index: 1000;
          background: rgba(30, 30, 30, 0.2);
        }
        .progress-bar {
          height: 100%;
          background: linear-gradient(90deg, #a855f7 0%, #c084fc 100%);
          width: 0%;
          transition: width 0.2s ease;
        }
        
        /* Achievement Animation */
        @keyframes bounce-subtle {
          0%, 100% { transform: translateY(0) translateX(-50%); }
          50% { transform: translateY(-10px) translateX(-50%); }
        }
        .animate-bounce-subtle {
          animation: bounce-subtle 1s ease-in-out;
        }
        
        /* Particle Cursor */
        .particle-cursor {
          pointer-events: none;
          position: fixed;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          z-index: 9999;
        }
        
        /* Pulse animation for interactive hints */
        @keyframes pulse {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        .animate-pulse {
          animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
        }

        /* --- Technology Platform Minimalist Animations --- */
        @keyframes platform-fade-in { from { opacity: 0; } to { opacity: 1; } }
        .animate-platform-fade-in { animation: platform-fade-in var(--platform-section-fade-in) ease-out forwards; }
        @keyframes platform-list-item-appear { from { opacity: 0; transform: translateX(-10px); } to { opacity: 1; transform: translateX(0px); } }
        /* Flowchart Animations */
        @keyframes flowchart-step-appear { from { opacity: 0; transform: translateY(10px) scale(0.95); } to { opacity: 1; transform: translateY(0) scale(1); } }
        .flowchart-svg.active .flowchart-step { opacity: 0; animation: flowchart-step-appear var(--platform-flow-node-appear) cubic-bezier(0.215, 0.610, 0.355, 1) forwards; /* animation-delay is set inline */ }
        @keyframes flowchart-line-draw { from { stroke-dashoffset: var(--line-length); } to { stroke-dashoffset: 0; } }
        .flowchart-svg.active .flowchart-line { animation: flowchart-line-draw var(--platform-flow-line-draw) cubic-bezier(0.65, 0, 0.35, 1) forwards; /* animation-delay is set inline */ }

        /* --- Foundry Visualizer Minimalist Animations --- */
        @keyframes core-enter-subtle { 0% { opacity: 0; transform: scale(0.6); filter: blur(4px); } 100% { opacity: 1; transform: scale(1); filter: blur(0px); } }
        .animate-core-enter-subtle { animation: core-enter-subtle var(--core-appear-duration) cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; }
        @keyframes core-spin-subtle { 0% { transform: rotate(0deg); } 100% { transform: rotate(-360deg); } }
        .core-spinner-subtle { animation: core-spin-subtle 25s linear infinite; transform-origin: center center; }
        @keyframes stream-enter-subtle-draw { from { stroke-dashoffset: 200; opacity: 0; } to { stroke-dashoffset: 0; opacity: 1; } }
        .animated-stream-path-subtle { stroke-dasharray: 200; stroke-dashoffset: 200; opacity: 0; animation: stream-enter-subtle-draw var(--stream-grow-duration) ease-out forwards; }
        .animate-stream-enter-subtle .animated-stream-path-subtle { } /* Triggered by parent */
        @keyframes pulse-subtle-anim { 0%, 100% { opacity: 0; transform: scale(0.8); } 50% { opacity: 0.8; transform: scale(1); } }
        .pulse-subtle { animation: pulse-subtle-anim 2s ease-in-out infinite; transform-origin: center center;}

        /* --- Anne SDR Visualizer Animations --- */
        @keyframes anne-core-enter {
            0% { opacity: 0; transform: scale(0.5); filter: blur(5px); }
            100% { opacity: 1; transform: scale(1); filter: blur(0px); }
        }

        @keyframes anne-core-pulse {
            0%, 100% { transform: scale(1); opacity: 0.7; }
            50% { transform: scale(1.05); opacity: 1; }
        }

        @keyframes anne-orbits-enter {
            0% { opacity: 0; }
            100% { opacity: 1; }
        }

        @keyframes anne-data-flow-enter {
            0% { opacity: 0; }
            100% { opacity: 1; }
        }

        .anne-core { opacity: 0; }
        .animate-anne-core-enter { animation: anne-core-enter 1s ease-out forwards; }
        .anne-core-pulse { animation: anne-core-pulse 3s ease-in-out infinite; }
        .anne-core-spinner { animation: spin 20s linear infinite; }

        .anne-orbits { opacity: 0; }
        .animate-anne-orbits-enter { animation: anne-orbits-enter 0.5s ease-out forwards; animation-delay: 0.5s; }

        .anne-orbit-icon { opacity: 0; }
        .animate-anne-orbits-enter .anne-orbit-icon { opacity: 1; }

        .anne-data-flow { opacity: 0; }
        .animate-anne-data-flow-enter { animation: anne-data-flow-enter 0.5s ease-out forwards; animation-delay: 1s; }

        .anne-flow-path { stroke-dasharray: 100; stroke-dashoffset: 100; }

        /* --- Ecosystem Visualizer Animations --- */
        @keyframes eco-hub-enter-anim { 0% { opacity: 0; transform: scale(0.4); } 100% { opacity: 1; transform: scale(1); } } @keyframes eco-hub-pulse-anim { 0%, 100% { transform: scale(1); filter: brightness(1); } 50% { transform: scale(1.03); filter: brightness(1.15); } } @keyframes eco-icon-enter-anim { 0% { opacity: 0; transform: scale(0.5) translateY(10px); } 100% { opacity: 1; transform: scale(1) translateY(0); } } @keyframes eco-icon-pulse-anim { 0%, 100% { transform: scale(1); } 50% { transform: scale(1.08); } } @keyframes eco-line-draw-anim { from { stroke-dashoffset: 100; } to { stroke-dashoffset: 0; } } .ecosystem-hub, .ecosystem-elements { opacity: 0; } .animate-ecosystem-hub-enter { animation: eco-hub-enter-anim 0.7s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards; animation-delay: 0.2s; } .animate-ecosystem-elements-enter { animation: anne-group-enter-anim 0.5s ease-out forwards; animation-delay: 0.5s; } .ecosystem-hub-pulse { transform-origin: center center; animation: eco-hub-pulse-anim var(--eco-hub-pulse-duration) ease-in-out infinite 1s; } .ecosystem-icon { transform-origin: center center; opacity: 0; } .animate-ecosystem-elements-enter .ecosystem-icon { opacity: 1; animation: eco-icon-pulse-anim var(--eco-icon-pulse-duration) ease-in-out infinite; animation-delay: calc(0.8s + var(--icon-delay, 0s)); } .ecosystem-line { stroke-dasharray: 100; stroke-dashoffset: 100; } .animate-ecosystem-elements-enter .ecosystem-line { animation: eco-line-draw-anim var(--eco-line-draw-duration) ease-out forwards; }

        /* --- Security Weave Visualizer Animations --- */
        @keyframes security-weave-enter-anim { 0% { opacity: 0; transform: scale(0.9); } 100% { opacity: 1; transform: scale(1); } } @keyframes security-weave-flow-anim { from { stroke-dashoffset: calc(var(--flow-offset-mult) * 100); } to { stroke-dashoffset: calc(var(--flow-offset-mult) * -100); } } @keyframes security-node-pulse-anim { 0%, 100% { r: 2.5; opacity: 0.8; filter: brightness(1); } 50% { r: 3.5; opacity: 1; filter: brightness(1.2); } } @keyframes security-spark-opacity-anim { 0%, 100% { opacity: 0; } 10%, 90% { opacity: 1; } } @keyframes security-glow-pulse-anim { 0%, 100% { filter: brightness(1) saturate(1); } 50% { filter: brightness(1.1) saturate(1.2); } } .security-weave-visualizer { opacity: 0; transform-origin: center center; } .animate-security-weave-enter { animation: security-weave-enter-anim 1.2s ease-out forwards; animation-delay: 0.3s; } .security-weave-paths { } .security-weave-line { stroke-dasharray: 15 5; stroke-dashoffset: calc(var(--flow-offset-mult) * 100); animation: security-weave-flow-anim var(--security-weave-flow-duration) linear infinite; animation-delay: var(--flow-delay); } .security-nodes { } .security-node { transform-origin: center center; animation: security-node-pulse-anim var(--security-node-pulse-duration) ease-in-out infinite; } .security-sparks { } .security-spark { opacity: 0; animation: security-spark-opacity-anim calc(var(--security-spark-travel-duration) * 0.3) ease-in-out infinite; }


        /* --- Set CSS variables from GLOBAL JS constants --- */
        :root {
            --core-appear-duration: ${GLOBAL_CORE_APPEAR_DURATION}s;
            --stream-grow-duration: ${GLOBAL_STREAM_GROW_DURATION}s;
            /* Platform timing */
            --platform-section-fade-in: ${GLOBAL_PLATFORM_SECTION_FADE_IN}s;
            --platform-list-item-stagger: ${GLOBAL_PLATFORM_LIST_ITEM_STAGGER}s;
            --platform-details-fade: ${GLOBAL_PLATFORM_DETAILS_FADE}s;
            --platform-flow-node-appear: ${GLOBAL_PLATFORM_FLOW_NODE_APPEAR}s;
            --platform-flow-node-stagger: ${GLOBAL_PLATFORM_FLOW_NODE_STAGGER}s;
            --platform-flow-line-draw: ${GLOBAL_PLATFORM_FLOW_LINE_DRAW}s;
            --platform-flow-line-delay: ${GLOBAL_PLATFORM_FLOW_LINE_DELAY}s;
            --platform-flow-line-stagger: ${GLOBAL_PLATFORM_FLOW_LINE_STAGGER}s;
            /* Ecosystem */
            --eco-hub-pulse-duration: ${GLOBAL_ECO_HUB_PULSE_DURATION}s;
            --eco-icon-pulse-duration: ${GLOBAL_ECO_ICON_PULSE_DURATION}s;
            --eco-line-draw-duration: ${GLOBAL_ECO_LINE_DRAW_DURATION}s;
            /* Security */
            --security-weave-flow-duration: ${GLOBAL_SECURITY_WEAVE_FLOW_DURATION}s;
            --security-spark-travel-duration: ${GLOBAL_SECURITY_SPARK_TRAVEL_DURATION}s;
            --security-node-pulse-duration: ${GLOBAL_SECURITY_NODE_PULSE_DURATION}s;
            --security-glow-pulse-duration: ${GLOBAL_SECURITY_GLOW_PULSE_DURATION}s;
        }
      `;
      document.head.appendChild(style);
    }

    // Page loaded - if not using initial animation, set immediately to true
    if (!pageLoaded) {
      // Uncomment below to bypass the loading animation during development
      // setPageLoaded(true);
    }

    // Add scroll progress bar for gamification
    const progressBar = document.createElement('div');
    progressBar.className = 'progress-bar-wrapper';
    progressBar.innerHTML = '<div class="progress-bar"></div>';
    document.body.appendChild(progressBar);

    const updateProgressBar = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollTop = document.documentElement.scrollTop || document.body.scrollTop;
      const progress = (scrollTop / scrollHeight) * 100;
      const progressBarElement = document.querySelector('.progress-bar');
      if (progressBarElement) {
        progressBarElement.style.width = `${progress}%`;
      }
    };

    window.addEventListener('scroll', updateProgressBar);
    updateProgressBar(); // Initialize on load

    return () => {
      window.removeEventListener('scroll', updateProgressBar);
      const progressBarWrapper = document.querySelector('.progress-bar-wrapper');
      if (progressBarWrapper) {
        progressBarWrapper.remove();
      }
    };

  }, [pageLoaded]);


  return (
    <div style={pageStyle} className="antialiased bg-black text-gray-300">
      {/* Loading Animation */}
      <LoadingAnimation onLoadComplete={() => setPageLoaded(true)} />
      
      {pageLoaded && (
        <>
      <div className="bg-black relative z-20">
          <AnnouncementBanner />
      </div>
      <HeroSection />
      <div className="bg-black">
        <StatsSection />
        <EchidnaFoundrySection />
        <AnneSDRSection />
        <TechnologyPlatformSection />
        <EcosystemSection />
        <SecurityInfraSection />
        <CallToActionSection />
        <FooterSection />
      </div>
      {showUpdatePopup && <UpdatePopup onClose={() => setShowUpdatePopup(false)} />}
        </>
      )}
    </div>
  );
}